import React, { useState, useEffect, useRef, FC, ChangeEvent, useMemo } from 'react';
import { View, Class, Student, Homework, UserSettings, Assignment, Submission, ClassSession, SessionGrade } from '../types';
import Card from './common/Card';
import { ArrowLeftIcon, UserGroupIcon, PlusIcon, PencilIcon, TrashIcon, ClipboardDocumentListIcon, XMarkIcon, LinkIcon, ClipboardDocumentCheckIcon, CheckIcon, ChartBarIcon, PhoneIcon, ArrowPathIcon, ArrowDownTrayIcon, ArrowUpTrayIcon, ChatBubbleBottomCenterTextIcon, MegaphoneIcon, MegaphoneIconSolid, EyeIcon, EyeSlashIcon, InformationCircleIcon, ExclamationTriangleIcon, MagnifyingGlassIcon } from './common/Icons';
import HelpOnboarding, { HelpStep } from './common/HelpOnboarding';
import { getUserLimits, checkLimit } from '../utils/limitUtils';
import { ClassSkeleton, TabSkeleton } from './common/Skeletons';

const teacherHelpSteps: HelpStep[] = [
    {
        title: 'Quản lý Lớp học',
        description: 'Tạo lớp mới, quản lý sĩ số học sinh và mời phụ huynh tham gia qua mã mời hoặc link chia sẻ.',
        icon: <UserGroupIcon className="h-12 w-12 text-indigo-600" />
    },
    {
        title: 'Giao & Chấm bài tập',
        description: 'Giao bài tập đa dạng từ thư viện hoặc tự tạo. Chấm điểm trực tiếp và gửi nhận xét tức thì cho học sinh và phụ huynh.',
        icon: <ClipboardDocumentListIcon className="h-12 w-12 text-indigo-600" />
    },
    {
        title: 'Báo cáo thông minh',
        description: 'Xem báo cáo chi tiết về điểm số, tỉ lệ chuyên cần và tiến độ học tập của cả lớp hoặc từng học sinh.',
        icon: <ChartBarIcon className="h-12 w-12 text-indigo-600" />
    }
];
import ClassModal from './common/ClassModal';
import StudentModal from './common/StudentModal';
import StudentReportModal from './common/StudentReportModal';
import Spinner from './common/Spinner';
import GradingModal from './common/GradingModal';
import LoadingDataIndicator from './common/LoadingDataIndicator';
import { AssignHomeworkModal } from './common/AssignHomeworkModal';
import { formatGrade, getStatusDisplay, getTypeColorConfig, isSubmissionGraded } from '../utils/assignmentUtils';
import { APP_MODE, EDU_CONFIG } from '../config';
import { 
    createAssignment, 
    addMultipleStudentsApi, 
    addStudentToClassApi, 
    removeStudentFromClassApi, 
    createClass as apiCreateClass, 
    updateClassInfo as apiUpdateClass, 
    deleteClassById as apiDeleteClass,
    getJoinRequests,
    getClassUpdates,
    approveJoinRequestApi,
    rejectJoinRequestApi,
    regenerateStudentLoginCodeApi,
    unlinkParentFromStudentApi,
    unlinkStudentAccountApi,
    uploadSubmissionAttachment,
    formatAssetUrl,
} from '../services/apiService';

const AddMultipleStudentsModal: React.FC<{
  isOpen: boolean;
  onClose: () => void;
  onSave: (students: any[]) => void;
  existingStudents: Student[];
  isSaving?: boolean;
}> = ({ isOpen, onClose, onSave, existingStudents, isSaving }) => {
    const [rawInput, setRawInput] = useState('');
    const [preview, setPreview] = useState<any[]>([]);
    const [errors, setErrors] = useState<Record<number, string[]>>({});

    const validateEmail = (email: string) => {
        if (!email) return true;
        return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
    };

    const validatePhone = (phone: string) => {
        if (!phone) return true;
        return /^[0-9+.\s-]{8,15}$/.test(phone);
    };

    const normalizeDate = (dateStr: string) => {
        if (!dateStr) return '';
        const trimmed = dateStr.trim();
        
        // case YYYY-MM-DD (already correct)
        if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) return trimmed;

        // Try to parse common formats: D/M/YYYY or M/D/YYYY
        // Using a simple split to avoid timezone issues with new Date()
        const parts = trimmed.split(/[\/\-\.]/);
        if (parts.length === 3) {
            let day, month, year;
            if (parts[0].length === 4) { // YYYY/MM/DD
                year = parts[0];
                month = parts[1].padStart(2, '0');
                day = parts[2].padStart(2, '0');
            } else if (parseInt(parts[1]) > 12) { // D/M/YYYY where M > 12 -> probably M/D/YYYY or D/M/YYYY confusion
                // Actually, if parts[1] > 12, it must be D/M/YYYY and parts[0] is month, or it's M/D/YYYY and parts[1] is day
                // Most common in VN is D/M/YYYY, but Excel often uses M/D/YYYY
                // Let's check if parts[0] > 12. If so, it's definitely D/M/YYYY
                if (parseInt(parts[0]) > 12) {
                    day = parts[0].padStart(2, '0');
                    month = parts[1].padStart(2, '0');
                    year = parts[2];
                } else {
                    // Ambiguous case like 5/6/2015. 
                    // However, the error report says '5/20/2015' -> M/D/YYYY
                    // If the second part is > 12, it's definitely the DAY in M/D/YYYY
                    month = parts[0].padStart(2, '0');
                    day = parts[1].padStart(2, '0');
                    year = parts[2];
                }
            } else {
                // Default to D/M/YYYY for Vietnam if not sure, 
                // but wait, '5/20/2015' was the error. In that case parts[1] is 20.
                // If parts[1] > 12, it's definitely M/D/YYYY (with 2nd part being day) or D/M/YYYY (with 1st part being day)
                // Let's use a more robust approach:
                if (parseInt(parts[0]) > 12) { // D/M/YYYY
                    day = parts[0].padStart(2, '0');
                    month = parts[1].padStart(2, '0');
                    year = parts[2];
                } else if (parseInt(parts[1]) > 12) { // M/D/YYYY
                    month = parts[0].padStart(2, '0');
                    day = parts[1].padStart(2, '0');
                    year = parts[2];
                } else {
                    // Still ambiguous (e.g., 01/02/2015). Assume D/M/YYYY as per VN standard
                    day = parts[0].padStart(2, '0');
                    month = parts[1].padStart(2, '0');
                    year = parts[2];
                }
            }
            
            // Ensure year is 4 digits
            if (year && year.length === 2) {
                year = (parseInt(year) > 30 ? '19' : '20') + year;
            }

            if (year && month && day) {
                return `${year}-${month}-${day}`;
            }
        }

        return trimmed;
    };

    useEffect(() => {
        if (!rawInput.trim()) {
            setPreview([]);
            setErrors({});
            return;
        }

        const lines = rawInput.split('\n').filter(line => line.trim());
        const newErrors: Record<number, string[]> = {};
        const seenInBatch = new Set<string>();
        
        const parsed = lines.map((line, index) => {
            let parts: string[] = [];
            if (line.includes('\t')) parts = line.split('\t');
            else if (line.includes(',')) parts = line.split(',');
            else parts = [line];

            const record = {
                name: parts[0]?.trim() || '',
                studentId: parts[1]?.trim() || '',
                email: parts[2]?.trim() || '',
                dob: normalizeDate(parts[3]?.trim()),
                parent_name: parts[4]?.trim() || '',
                parent_phone: parts[5]?.trim() || '',
                parent_relationship: parts[6]?.trim() || '',
                parent_name_2: parts[7]?.trim() || '',
                parent_phone_2: parts[8]?.trim() || '',
                parent_relationship_2: parts[9]?.trim() || '',
            };

            const rowErrors: string[] = [];
            if (!record.name) rowErrors.push('Thiếu tên học sinh');
            if (record.email) {
                if (!validateEmail(record.email)) {
                    rowErrors.push('Email không hợp lệ');
                } else {
                    const upperEmail = record.email.toUpperCase();
                    if (existingStudents.some(es => String(es.email || '').toUpperCase() === upperEmail)) {
                        rowErrors.push(`Email "${record.email}" đã tồn tại trên hệ thống`);
                    }
                }
            }
            if (record.dob && !/^\d{4}-\d{2}-\d{2}$/.test(record.dob)) rowErrors.push('Ngày sinh không hợp lệ (Dùng DD/MM/YYYY hoặc YYYY-MM-DD)');
            
            // Check for duplicate student code
            if (record.studentId) {
                const upperId = record.studentId.toUpperCase();
                
                // Check in existing database
                const isDuplicateInDb = existingStudents.some(es => 
                    String(es.studentId || (es as any).student_id_code || '').toUpperCase() === upperId
                );
                
                if (isDuplicateInDb) {
                    rowErrors.push(`Mã học sinh "${record.studentId}" đã tồn tại trên hệ thống`);
                } else if (seenInBatch.has(upperId)) {
                    // Check within the current import list
                    rowErrors.push(`Mã học sinh "${record.studentId}" bị lặp lại trong danh sách nhập này`);
                }
                
                seenInBatch.add(upperId);
            }

            if (record.parent_phone && !validatePhone(record.parent_phone)) rowErrors.push('SĐT PH1 không hợp lệ');
            if (record.parent_phone_2 && !validatePhone(record.parent_phone_2)) rowErrors.push('SĐT PH2 không hợp lệ');
            
            if (rowErrors.length > 0) {
                newErrors[index] = rowErrors;
            }

            return record;
        }).filter(s => s.name || s.studentId);

        setPreview(parsed);
        setErrors(newErrors);
    }, [rawInput]);

    const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;

        const reader = new FileReader();
        reader.onload = (event) => {
            const content = event.target?.result as string;
            // Remove header if it exists
            const lines = content.split('\n');
            if (lines.length > 0 && (lines[0].toLowerCase().includes('họ tên') || lines[0].toLowerCase().includes('name'))) {
                setRawInput(lines.slice(1).join('\n'));
            } else {
                setRawInput(content);
            }
        };
        reader.readAsText(file, 'UTF-8');
    };

    const handleSave = () => {
        const hasErrors = Object.keys(errors).length > 0;
        if (hasErrors) {
            if (!confirm('Dữ liệu vẫn còn lỗi. Bạn có chắc chắn muốn nhập các bản ghi hợp lệ?')) {
                return;
            }
        }

        const validStudents = preview.filter((_, index) => !errors[index]);
        if (validStudents.length > 0) {
            onSave(validStudents);
            setRawInput('');
        } else {
            alert('Không có bản ghi hợp lệ để nhập.');
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm" onClick={onClose}>
            <div className="bg-white rounded-3xl shadow-2xl max-w-4xl w-full m-4 flex flex-col max-h-[90vh]" onClick={e => e.stopPropagation()}>
                <div className="p-6 border-b flex justify-between items-center bg-teal-600 text-white rounded-t-3xl">
                    <div>
                        <h3 className="text-xl font-bold uppercase tracking-widest">Nhập liệu thông minh</h3>
                        <p className="text-xs text-teal-100 font-medium">Hỗ trợ Copy-Paste từ Excel, Google Sheets hoặc CSV</p>
                    </div>
                    <button onClick={onClose} className="p-2 hover:bg-white/20 rounded-full transition-colors"><XMarkIcon className="h-6 w-6 text-white" /></button>
                </div>
                
                <div className="p-6 grid grid-cols-1 lg:grid-cols-2 gap-6 overflow-hidden">
                    <div className="flex flex-col h-full">
                        <div className="flex justify-between items-end mb-2">
                            <label className="block text-xs font-semibold text-slate-500">Dán dữ liệu hoặc tải file</label>
                            <label className="flex items-center gap-1.5 px-3 py-1 bg-teal-50 text-teal-600 rounded-lg text-[10px] font-bold cursor-pointer hover:bg-teal-100 transition-all">
                                <ArrowUpTrayIcon className="h-3 w-3" />
                                <span>Tải lên .csv</span>
                                <input type="file" accept=".csv" className="hidden" onChange={handleFileUpload} />
                            </label>
                        </div>
                        <textarea
                            className="flex-grow w-full p-4 bg-slate-50 border-2 border-slate-100 rounded-2xl focus:border-teal-500 focus:bg-white transition-all font-mono text-[10px] outline-none resize-none"
                            placeholder="Tên đầy đủ	Mã số	Email	Ngày sinh	Tên PH1	SĐT PH1	Quan hệ	Tên PH2	SĐT PH2	Quan hệ&#10;Nguyễn Văn An	HS001	an@gmail.com	2015-05-20	Nguyễn Văn Hùng	0901234567	Bố	Trần Thị Lan	0907654321	Mẹ"
                            value={rawInput}
                            onChange={e => setRawInput(e.target.value)}
                        />
                            <div className="mt-4 p-4 bg-amber-50/50 rounded-2xl border border-amber-100">
                                <p className="text-[10px] text-amber-700 font-medium leading-relaxed">
                                    <span className="font-bold uppercase">💡 Mẹo:</span> Copy cả bảng từ Excel và dán trực tiếp vào ô trên. 
                                    Hệ thống tự tách: <span className="font-bold">Tên đầy đủ, Mã số, Email, Ngày sinh, 2 Phụ huynh (Tên, SĐT, Quan hệ).</span>
                                </p>
                            </div>
                    </div>

                    <div className="flex flex-col h-full overflow-hidden">
                        <div className="flex justify-between items-end mb-2">
                            <label className="block text-xs font-semibold text-slate-500">Xem trước kết quả ({preview.length})</label>
                            {Object.keys(errors).length > 0 && (
                                <span className="text-[10px] font-bold text-rose-500 bg-rose-50 px-2 py-0.5 rounded-full border border-rose-100 animate-pulse">
                                    Phát hiện {Object.keys(errors).length} bản ghi có lỗi
                                </span>
                            )}
                        </div>
                        <div className="flex-grow bg-slate-50 rounded-2xl border-2 border-slate-100 overflow-y-auto custom-scrollbar">
                            {preview.length > 0 ? (
                                <table className="w-full text-[10px] border-collapse">
                                    <thead className="sticky top-0 bg-white border-b shadow-sm">
                                        <tr>
                                            <th className="p-2 text-left font-bold text-slate-400 uppercase tracking-tighter">Học sinh</th>
                                            <th className="p-2 text-left font-bold text-slate-400 uppercase tracking-tighter">Ngày sinh</th>
                                            <th className="p-2 text-left font-bold text-slate-400 uppercase tracking-tighter">Phụ huynh 1</th>
                                            <th className="p-2 text-left font-bold text-slate-400 uppercase tracking-tighter">Phụ huynh 2</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-slate-100">
                                        {preview.map((s, i) => (
                                            <tr key={i} className={`hover:bg-teal-50/50 transition-colors ${errors[i] ? 'bg-rose-50/50' : ''}`}>
                                                <td className="p-2">
                                                    <div className="flex items-center gap-1.5">
                                                        {errors[i] && (
                                                            <div className="relative group/error">
                                                                <XMarkIcon className="h-3.5 w-3.5 text-rose-500 bg-rose-100 rounded-full p-0.5" />
                                                                <div className="absolute bottom-full left-0 mb-2 w-48 p-2 bg-slate-800 text-white text-[9px] rounded-lg opacity-0 group-hover/error:opacity-100 transition-opacity z-10 pointer-events-none shadow-xl">
                                                                    <p className="font-bold mb-1 text-rose-300">Lỗi dữ liệu:</p>
                                                                    <ul className="list-disc ml-3">
                                                                        {errors[i].map((err, idx) => <li key={idx}>{err}</li>)}
                                                                    </ul>
                                                                </div>
                                                            </div>
                                                        )}
                                                        <div>
                                                            <div className={`font-bold ${errors[i] ? 'text-rose-700' : 'text-slate-700'}`}>{s.name || '---'}</div>
                                                            <div className="text-[9px] text-slate-400">{s.studentId || '---'}</div>
                                                        </div>
                                                    </div>
                                                </td>
                                                <td className="p-2 text-slate-500">{s.dob || '---'}</td>
                                                <td className="p-2">
                                                    <div className="font-bold text-slate-700">{s.parent_name || '---'}</div>
                                                    <div className={`text-[9px] ${errors[i]?.some(e => e.includes('PH1')) ? 'text-rose-500 font-bold' : 'text-teal-600'}`}>{s.parent_relationship || ''} {s.parent_phone}</div>
                                                </td>
                                                <td className="p-2">
                                                    <div className="font-bold text-slate-700">{s.parent_name_2 || '---'}</div>
                                                    <div className={`text-[9px] ${errors[i]?.some(e => e.includes('PH2')) ? 'text-rose-500 font-bold' : 'text-teal-600'}`}>{s.parent_relationship_2 || ''} {s.parent_phone_2}</div>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            ) : (
                                <div className="h-full flex flex-col items-center justify-center text-slate-300 p-8 text-center">
                                    <ClipboardDocumentListIcon className="h-12 w-12 mb-2 opacity-20" />
                                    <p className="text-sm font-medium">Dữ liệu sau khi nhập sẽ hiển thị xem trước tại đây</p>
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                <div className="p-6 bg-slate-50 border-t flex justify-between items-center rounded-b-3xl">
                    <button 
                        onClick={() => {
                            const csvHeader = "Họ tên,Mã số,Email,Ngày sinh,Tên Phụ huynh 1,SĐT Phụ huynh 1,Quan hệ PH1,Tên Phụ huynh 2,SĐT Phụ huynh 2,Quan hệ PH2";
                            const csvRows = "Nguyễn Văn An,HS001,an@school.edu.vn,2015-05-20,Nguyễn Văn Hùng,0901234567,Bố,Trần Thị Lan,0907654321,Mẹ\nTrần Thị Bình,HS002,binh@school.edu.vn,2015-08-15,Trần Văn Nam,0902223333,Bố,,,\n";
                            const csvContent = "\uFEFF" + csvHeader + "\n" + csvRows;
                            const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
                            const url = URL.createObjectURL(blob);
                            const link = document.createElement("a");
                            link.setAttribute("href", url);
                            link.setAttribute("download", "mau_nhap_lieu_hoc_sinh.csv");
                            document.body.appendChild(link);
                            link.click();
                            document.body.removeChild(link);
                            URL.revokeObjectURL(url);
                        }}
                        className="text-xs font-bold text-teal-600 hover:underline"
                    >
                        Tải file mẫu (.csv)
                    </button>
                    <div className="flex gap-3">
                        <button onClick={onClose} disabled={isSaving} className="px-6 py-2 bg-white border border-slate-200 text-slate-500 font-bold text-sm rounded-xl hover:bg-slate-100 transition-all">Hủy</button>
                        <button 
                            onClick={handleSave} 
                            disabled={isSaving || preview.length === 0} 
                            className="flex items-center gap-2 px-8 py-2 bg-teal-600 text-white font-bold text-sm rounded-xl hover:bg-teal-700 shadow-lg shadow-teal-100 active:scale-95 disabled:opacity-50 transition-all"
                        >
                            {isSaving && <Spinner className="h-4 w-4" />}
                            <span>Nhập {preview.length} học sinh</span>
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

interface ClassManagementProps {
  onBack: () => void;
  onNavigate: (view: View, payload?: object) => void;
  user: UserSettings;
  classes: Class[];
  students: Student[];
  homeworks: Homework[];
  assignments: Assignment[];
  submissions: Submission[];
  joinRequests: any[];
  onSaveClass: (classData: Omit<Class, 'id' | 'studentIds' | 'inviteCode'>) => Promise<Class>;
  onUpdateClass: (classData: Class, skipSync?: boolean) => Promise<void>;
  onDeleteClass: (id: string | number, skipSync?: boolean) => void;
  onSaveStudent: (studentData: Omit<Student, 'id'>, skipSync?: boolean) => Promise<Student>;
  onSaveMultipleStudents: (classId: string | number, students: Partial<Student>[], syncedStudents?: Student[]) => Promise<void>;
  onUpdateStudent: (studentData: Student, skipSync?: boolean) => Promise<void>;
  onDeleteStudent: (id: string | number, skipSync?: boolean) => void;
  onAssignHomework: (assignment: Partial<Assignment>, skipSync?: boolean) => void;
  onDeleteAssignment: (id: string, force?: boolean) => Promise<{ success: boolean; conflict?: boolean; message?: string; submissionCount?: number }>;
  onGradeSubmission: (submissionId: string, grade: number, teacherComments: string, skipSync?: boolean) => void;
  onRefreshClassUpdates?: (classId: string | number, removeRequestId?: string | number) => Promise<void>;
  onRefreshJoinRequests?: () => void;
  onUpdateSettings?: React.Dispatch<React.SetStateAction<UserSettings | null>>;
  isLoading?: boolean;
  activeClass?: Class;
}

// Mock data for demonstration purposes (DEVELOPMENT only)
const MOCK_ONLINE_PENDING_STUDENTS: Student[] = [
    { id: 'pending_1', name: 'Trần Minh Anh' },
    { id: 'pending_2', name: 'Lê Thị Bích' },
];

const MOCK_ONLINE_APPROVED_STUDENTS: Student[] = [
    { id: 'approved_1', name: 'Nguyễn Văn Hùng' },
];

const MOCK_PENDING_REQUESTS = [
    { id: 'req1', parentName: 'Trần Văn Tuấn', parentPhone: '0912345678', studentName: 'Nguyễn Văn An', studentId: 's1', type: 'parent_link' },
    { id: 'req2', parentName: 'Lê Thị Hồng', parentPhone: '0988777666', studentName: 'Lê Văn Cường', studentId: 's3', type: 'parent_link' }
];


const SessionModal: React.FC<{
    isOpen: boolean;
    onClose: () => void;
    onSave: (session: ClassSession) => void;
    session?: ClassSession | null;
    students: Student[];
    isSaving?: boolean;
}> = ({ isOpen, onClose, onSave, session, students, isSaving }) => {
    const [title, setTitle] = useState('');
    const [date, setDate] = useState('');
    const [grades, setGrades] = useState<SessionGrade[]>([]);
    const [errors, setErrors] = useState<Record<string, string>>({});

    useEffect(() => {
        if (isOpen) {
            if (session) {
                setTitle(session.title);
                setDate(session.date);
                setGrades(session.grades || []);
            } else {
                setTitle(`Buổi học ngày ${new Date().toLocaleDateString('vi-VN')}`);
                setDate(new Date().toISOString().split('T')[0]);
                setGrades(students.map(s => ({
                    studentId: s.id,
                    attendance: 'present',
                    grade: ''
                })));
            }
            setErrors({});
        }
    }, [session, students, isOpen]);

    const handleSave = () => {
        const newErrors: Record<string, string> = {};
        if (!title.trim()) newErrors.title = 'Vui lòng nhập tên buổi học';
        if (!date) newErrors.date = 'Vui lòng chọn ngày học';
        
        if (Object.keys(newErrors).length > 0) {
            setErrors(newErrors);
            return;
        }

        onSave({
            id: session?.id || `sess-${Date.now()}`,
            title: title.trim(),
            date,
            grades
        });
    };

    const handleUpdateGrade = (studentId: string | number, field: keyof SessionGrade, value: any) => {
        setGrades(prev => prev.map(g => String(g.studentId) === String(studentId) ? { ...g, [field]: value } : g));
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-60 overflow-y-auto py-8" onClick={onClose}>
            <div className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full m-4 flex flex-col max-h-full overflow-hidden" onClick={e => e.stopPropagation()}>
                <div className="p-6 border-b flex justify-between items-center bg-teal-600 text-white rounded-t-2xl">
                    <h3 className="text-xl font-bold uppercase tracking-widest">{session ? 'Cập nhật buổi học' : 'Thêm buổi học mới'}</h3>
                    <button onClick={onClose} className="p-2 hover:bg-white/20 rounded-full transition-colors"><XMarkIcon className="h-6 w-6 text-white" /></button>
                </div>
                <div className="p-6 space-y-6 overflow-y-auto flex-grow">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                            <label className="block text-xs font-semibold text-slate-500 mb-1">Tên buổi học / Chủ đề *</label>
                            <input type="text" value={title} onChange={e => { setTitle(e.target.value); setErrors(prev => ({ ...prev, title: '' })); }} className={`w-full p-3 bg-slate-50 border-2 rounded-xl focus:border-teal-500 focus:bg-white transition-all font-bold text-sm ${errors.title ? 'border-rose-300 bg-rose-50/30' : 'border-slate-100'}`} placeholder="VD: Ôn tập chương 1" />
                            {errors.title && <p className="mt-1 text-[10px] text-rose-600 font-bold">{errors.title}</p>}
                        </div>
                        <div>
                            <label className="block text-xs font-semibold text-slate-500 mb-1">Ngày học *</label>
                            <input type="date" value={date} onChange={e => { setDate(e.target.value); setErrors(prev => ({ ...prev, date: '' })); }} className={`w-full p-3 bg-slate-50 border-2 rounded-xl focus:border-teal-500 focus:bg-white transition-all font-bold text-sm ${errors.date ? 'border-rose-300 bg-rose-50/30' : 'border-slate-100'}`} />
                            {errors.date && <p className="mt-1 text-[10px] text-rose-600 font-bold">{errors.date}</p>}
                        </div>
                    </div>

                    <div className="space-y-4">
                        <h4 className="text-xs font-black text-slate-400 uppercase tracking-widest border-b pb-2">Danh sách điểm danh & Nhận xét</h4>
                        <div className="overflow-x-auto">
                            <table className="w-full text-sm">
                                <thead className="bg-slate-50">
                                    <tr>
                                        <th className="text-left p-3 text-[10px] font-bold text-slate-400 uppercase">Học sinh</th>
                                        <th className="text-center p-3 text-[10px] font-bold text-slate-400 uppercase">Chuyên cần</th>
                                        <th className="text-center p-3 text-[10px] font-bold text-slate-400 uppercase">Điểm (0-10)</th>
                                        <th className="text-left p-3 text-[10px] font-bold text-slate-400 uppercase">Ghi chú</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-slate-100">
                                    {students.map(student => {
                                        const grade = grades.find(g => g.studentId === student.id) || { studentId: student.id, attendance: 'present', grade: '' };
                                        return (
                                            <tr key={student.id} className="hover:bg-slate-50/50">
                                                <td className="p-3 font-bold text-slate-700">
                                                    {student.name}
                                                    {student.realName && (
                                                        <span className="block text-[10px] text-slate-400 font-medium italic">({student.realName})</span>
                                                    )}
                                                </td>
                                                <td className="p-3">
                                                    <div className="flex justify-center gap-1">
                                                        {[
                                                            { val: 'present', label: 'Có mặt', color: 'bg-green-100 text-green-700' },
                                                            { val: 'late', label: 'Muộn', color: 'bg-amber-100 text-amber-700' },
                                                            { val: 'absent', label: 'Vắng', color: 'bg-red-100 text-red-700' },
                                                        ].map(opt => (
                                                            <button 
                                                                key={opt.val}
                                                                onClick={() => handleUpdateGrade(student.id, 'attendance', opt.val)}
                                                                className={`px-2 py-1 text-[10px] font-bold rounded-lg transition-all ${
                                                                    grade.attendance === opt.val ? opt.color : 'bg-slate-100 text-slate-400 hover:bg-slate-200'
                                                                }`}
                                                            >
                                                                {opt.label}
                                                            </button>
                                                        ))}
                                                    </div>
                                                </td>
                                                <td className="p-3">
                                                    <input 
                                                        type="text" 
                                                        value={grade.grade} 
                                                        onChange={e => handleUpdateGrade(student.id, 'grade', e.target.value)}
                                                        className="w-16 mx-auto p-1 bg-white border border-slate-200 rounded text-center font-bold text-teal-600 text-sm"
                                                        placeholder="-"
                                                    />
                                                </td>
                                                <td className="p-3">
                                                    <input 
                                                        type="text" 
                                                        value={grade.note || ''} 
                                                        onChange={e => handleUpdateGrade(student.id, 'note', e.target.value)}
                                                        className="w-full p-2 bg-white border border-slate-200 rounded-lg text-xs font-medium"
                                                        placeholder="Nhận xét..."
                                                    />
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
                <div className="p-6 bg-slate-50 border-t flex justify-end gap-3 rounded-b-2xl">
                    <button onClick={onClose} disabled={isSaving} className="px-6 py-2 bg-white border border-slate-200 text-slate-500 font-bold text-sm rounded-xl hover:bg-slate-100 transition-all">Hủy</button>
                    <button 
                        disabled={isSaving}
                        onClick={handleSave} 
                        className="flex items-center gap-2 px-10 py-2 bg-teal-600 text-white font-bold text-sm rounded-xl hover:bg-teal-700 shadow-lg shadow-teal-100 active:scale-95 transition-all disabled:opacity-50"
                    >
                        {isSaving && <Spinner className="h-4 w-4" />}
                        <span>Lưu buổi học</span>
                    </button>
                </div>
            </div>
        </div>
    );
};

const ClassManagement: React.FC<ClassManagementProps> = (props) => {
    const { onBack, onNavigate, user, classes, students, homeworks, assignments, submissions, joinRequests, onSaveClass, onUpdateClass, onDeleteClass, onSaveStudent, onSaveMultipleStudents, onUpdateStudent, onDeleteStudent, onAssignHomework, onDeleteAssignment, onGradeSubmission, onRefreshClassUpdates, onRefreshJoinRequests, onUpdateSettings, isLoading } = props;

    const limits = getUserLimits(user);

    const handleExportBackup = () => {
        const backupData = {
            classes,
            students,
            assignments,
            submissions,
            exportDate: new Date().toISOString(),
            userId: user.id
        };
        const blob = new Blob([JSON.stringify(backupData, null, 2)], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `bookai_backup_${new Date().toISOString().split('T')[0]}.json`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
    };

    const handleImportBackup = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;

        const reader = new FileReader();
        reader.onload = async (event) => {
            try {
                const data = JSON.parse(event.target?.result as string);
                if (!data.classes || !data.students) {
                    throw new Error("Định dạng tệp không hợp lệ");
                }

                if (confirm(`Bạn có chắc muốn nhập dữ liệu từ tệp sao lưu? Dữ liệu hiện tại sẽ được hợp nhất. (Bao gồm ${data.classes.length} lớp và ${data.students.length} học sinh)`)) {
                    // This logic would ideally call a bulk sync API or update local state
                    // For now, let's trigger a reload notification
                    alert("Tính năng nhập dữ liệu đang được đồng bộ hóa. Các lớp và học sinh mới sẽ được thêm vào danh sách của bạn.");
                    
                    // In a real implementation, we would call onSaveClass and onSaveStudent for each item
                    // For demo/offline purposes, we'll assume the user will manually sync or the app handles it
                }
            } catch (err) {
                alert("Lỗi khi đọc tệp sao lưu: " + (err as Error).message);
            }
        };
        reader.readAsText(file);
    };

    const [isClassModalOpen, setIsClassModalOpen] = useState(false);
    const [classToEdit, setClassToEdit] = useState<Class | null>(null);

    const [isStudentModalOpen, setIsStudentModalOpen] = useState(false);
    const [isMultiStudentModalOpen, setIsMultiStudentModalOpen] = useState(false);
    const [studentToEdit, setStudentToEdit] = useState<Student | null>(null);
    const [currentClassIdForStudent, setCurrentClassIdForStudent] = useState<string | null>(null);

    const [isAssignModalOpen, setIsAssignModalOpen] = useState(false);
    const [assignModalType, setAssignModalType] = useState<'homework' | 'task' | 'announcement'>('homework');
    const [classToAssign, setClassToAssign] = useState<string | null>(null);
    const [copiedLink, setCopiedLink] = useState('');
    const [copiedStudentLoginId, setCopiedStudentLoginId] = useState<string>('');
    const [regeneratingStudentId, setRegeneratingStudentId] = useState<string | null>(null);
    const [isSavingClass, setIsSavingClass] = useState(false);
    const [isSavingStudent, setIsSavingStudent] = useState(false);
    const [isSavingAssignment, setIsSavingAssignment] = useState(false);
    const [processingRequestId, setProcessingRequestId] = useState<string | null>(null);
    const [rejectModalRequestId, setRejectModalRequestId] = useState<string | null>(null);
    const [rejectReason, setRejectReason] = useState('');
    const [unlinkModalData, setUnlinkModalData] = useState<{ classId: string|number, studentId: string|number, parentUserId: number, parentName: string, studentName: string } | null>(null);
    const [unlinkReason, setUnlinkReason] = useState('');

    const [isReportModalOpen, setIsReportModalOpen] = useState(false);
    const [studentForReport, setStudentForReport] = useState<Student | null>(null);
    const [activeTabs, setActiveTabs] = useState<Record<string, string>>({}); // Track active tab per class
    const [isSessionModalOpen, setIsSessionModalOpen] = useState(false);
    const [currentSessionClassId, setCurrentSessionClassId] = useState<string | null>(null);
    const [editingSession, setEditingSession] = useState<ClassSession | null>(null);
    const [gradingSubmission, setGradingSubmission] = useState<{homework: Homework, submission: Submission, student: Student, assignment?: Assignment} | null>(null);
    const [editingAssignment, setEditingAssignment] = useState<Assignment | null>(null);
    const [conflictModalData, setConflictModalData] = useState<{ id: string, message: string, count: number } | null>(null);

    const filteredClasses = useMemo(() => {
        if (props.activeClass) {
            return [props.activeClass];
        }
        return classes;
    }, [classes, props.activeClass]);

    const getActiveTab = (classId: string) => activeTabs[classId] || 'students';
    const setActiveTab = (classId: string, tab: string) => setActiveTabs(prev => ({ ...prev, [classId]: tab }));

    useEffect(() => {
        const { classId, activeTab } = (props as any);
        if (classId && activeTab) {
            setActiveTabs(prev => ({ ...prev, [String(classId)]: activeTab }));
        }
    }, []);

    const handleAddSession = (classId: string) => {
        setCurrentSessionClassId(classId);
        setEditingSession(null);
        setIsSessionModalOpen(true);
    };

    const handleEditSession = (classId: string, session: ClassSession) => {
        setCurrentSessionClassId(classId);
        setEditingSession(session);
        setIsSessionModalOpen(true);
    };

    const handleSaveSession = (classId: string, sessionData: ClassSession) => {
        const cls = classes.find(c => c.id === classId);
        if (cls) {
            const updatedSessions = cls.sessions ? [...cls.sessions] : [];
            const existingIdx = updatedSessions.findIndex(s => s.id === sessionData.id);
            if (existingIdx >= 0) {
                updatedSessions[existingIdx] = sessionData;
            } else {
                updatedSessions.push(sessionData);
            }
            onUpdateClass({ ...cls, sessions: updatedSessions });
        }
        setIsSessionModalOpen(false);
    };

    const [pendingStudents, setPendingStudents] = useState<Student[]>([]);
    const [approvedStudents, setApprovedStudents] = useState<Student[]>([]);
    const [pendingRequests, setPendingRequests] = useState<any[]>([]);
    const [isFetchingRequests, setIsFetchingRequests] = useState(false);
    const [visibleLoginCodes, setVisibleLoginCodes] = useState<Record<string, boolean>>({});
    const [refreshCooldown, setRefreshCooldown] = useState(0);
    const [isRefreshingRequests, setIsRefreshingRequests] = useState(false);

    const toggleLoginCodeVisibility = (studentId: string) => {
        setVisibleLoginCodes(prev => ({ ...prev, [studentId]: !prev[studentId] }));
    };

    // Manual refresh with cooldown (in seconds)
    const handleRefreshManual = async () => {
        if (refreshCooldown > 0 || !onRefreshJoinRequests || isRefreshingRequests) return;
        
        setIsRefreshingRequests(true);
        try {
            await onRefreshJoinRequests();
            setRefreshCooldown(15); // Wait 15s before next refresh
        } finally {
            setIsRefreshingRequests(false);
        }
    };

    useEffect(() => {
        let timer: any;
        if (refreshCooldown > 0) {
            timer = setInterval(() => {
                setRefreshCooldown(prev => prev - 1);
            }, 1000);
        }
        return () => clearInterval(timer);
    }, [refreshCooldown]);

    useEffect(() => {
        if (joinRequests && Array.isArray(joinRequests)) {
            setPendingRequests(joinRequests);
        } else if (APP_MODE !== 'PRODUCTION') {
            setPendingRequests(MOCK_PENDING_REQUESTS);
        }
    }, [joinRequests]);

    // Removed automatic polling for join requests to reduce server load

    const handleApproveRequest = async (requestId: string) => {
        const req = pendingRequests.find(r => r.id === requestId);
        if (req) {
            setProcessingRequestId(requestId);
            if (APP_MODE === 'PRODUCTION') {
                try {
                    const success = await approveJoinRequestApi(requestId);
                    if (success) {
                        setPendingRequests(prev => prev.filter(r => String(r.id) !== String(requestId)));
                        alert(`Đã phê duyệt yêu cầu từ phụ huynh ${req.parentName}`);
                        const classId = req.classId || req.class_id;
                        if (classId && onRefreshClassUpdates) {
                            await onRefreshClassUpdates(classId, requestId);
                        } else if (typeof (props as any).onRefreshData === 'function') {
                            (props as any).onRefreshData();
                        }
                    } else {
                        alert('Không thể phê duyệt yêu cầu này.');
                    }
                } catch (e) {
                    alert('Lỗi khi phê duyệt yêu cầu.');
                } finally {
                    setProcessingRequestId(null);
                }
            } else {
                alert(`Đã phê duyệt Phụ huynh ${req.parentName} là phụ huynh của học sinh ${req.studentName}`);
                setPendingRequests(prev => prev.filter(r => r.id !== requestId));
                setProcessingRequestId(null);
            }
        }
    };

    const handleOpenRejectModal = (requestId: string) => {
        setRejectModalRequestId(requestId);
        setRejectReason('');
    };

    const handleRejectRequest = async (requestId: string, reason?: string) => {
        setRejectModalRequestId(null);
        setProcessingRequestId(requestId);
        if (APP_MODE === 'PRODUCTION') {
            try {
                const success = await rejectJoinRequestApi(requestId, reason);
                if (success) {
                    setPendingRequests(prev => prev.filter(r => String(r.id) !== String(requestId)));
                    const classId = (pendingRequests.find(r => String(r.id) === String(requestId)) as any)?.classId || (pendingRequests.find(r => String(r.id) === String(requestId)) as any)?.class_id;
                    if (classId && onRefreshClassUpdates) {
                        await onRefreshClassUpdates(classId, requestId);
                    } else if (typeof (props as any).onRefreshData === 'function') {
                        (props as any).onRefreshData();
                    }
                }
            } catch (e) {
                alert('Lỗi khi từ chối yêu cầu.');
            } finally {
                setProcessingRequestId(null);
            }
        } else {
            setPendingRequests(prev => prev.filter(r => r.id !== requestId));
            setProcessingRequestId(null);
        }
    };

    const handleUnlinkParent = async (classId: string|number, studentId: string|number, parentUserId: number, reason?: string) => {
        setUnlinkModalData(null);
        setUnlinkReason('');
        if (APP_MODE === 'PRODUCTION') {
            try {
                const r = await unlinkParentFromStudentApi(classId, studentId, parentUserId, reason);
                if (r.success) {
                    onRefreshJoinRequests?.();
                    await onRefreshClassUpdates?.(classId);
                    alert('Đã hủy liên kết thành công.');
                } else {
                    alert(r.message || 'Không thể hủy liên kết');
                }
            } catch (e) {
                alert('Lỗi khi hủy liên kết.');
            }
        }
    };

    // Initialize mock data for Online class demo if empty
    useEffect(() => {
        if (approvedStudents.length === 0 && APP_MODE === 'DEVELOPMENT') {
            setPendingStudents(MOCK_ONLINE_PENDING_STUDENTS);
            setApprovedStudents(MOCK_ONLINE_APPROVED_STUDENTS);
        }
    }, []);

    const handleAccept = (studentId: string) => {
        const studentToMove = pendingStudents.find(s => s.id === studentId);
        if (studentToMove) {
            setPendingStudents(prev => prev.filter(s => s.id !== studentId));
            setApprovedStudents(prev => [...prev, studentToMove]);
        }
    };

    const handleDecline = (studentId: string) => {
        if (confirm('Bạn có chắc muốn từ chối yêu cầu tham gia này?')) {
            setPendingStudents(prev => prev.filter(s => s.id !== studentId));
        }
    };

    const handleRemoveOnlineMember = (studentId: string) => {
        const studentToRemove = approvedStudents.find(s => s.id === studentId);
        if (studentToRemove && confirm(`Bạn có chắc muốn xóa học sinh "${studentToRemove.name}" khỏi lớp?`)) {
            setApprovedStudents(prev => prev.filter(s => s.id !== studentId));
        }
    };

    const handleRemoveStudentFromOfflineClass = async (classId: string, studentId: string) => {
        const classToUpdate = classes.find(c => c.id === classId);
        const student = students.find(s => s.id === studentId);
        if (classToUpdate && student && confirm(`Bạn có chắc muốn xóa học sinh "${student.name}" khỏi lớp "${classToUpdate.name}"?`)) {
            if (APP_MODE === 'PRODUCTION') {
                try { await removeStudentFromClassApi(classId, studentId); } catch (e: any) { alert(e?.message || 'Không thể xóa học sinh khỏi lớp.'); return; }
            }
            const updatedStudentIds = classToUpdate.studentIds.filter(id => id !== studentId);
            onUpdateClass({ ...classToUpdate, studentIds: updatedStudentIds }, true);
        }
    };


    const handleSaveClass = async (classData: Omit<Class, 'id' | 'studentIds' | 'inviteCode'>) => {
        if (!classToEdit && !checkLimit(classes.length, limits.maxClasses)) {
            alert(`Bạn đã đạt giới hạn tối đa ${limits.maxClasses} lớp học. Vui lòng nâng cấp gói để tiếp tục.`);
            onNavigate(View.CreditStore);
            return;
        }
        setIsSavingClass(true);
        if (APP_MODE === 'PRODUCTION') {
            try {
                if (classToEdit) {
                    await apiUpdateClass(classToEdit.id, {
                        name: classData.name,
                        academic_year: classData.academicYear || null,
                        grade_level: (classData.gradeLevel as any) || null,
                        class_type: classData.classType as any,
                        allow_join_from_outside: (classData as any).allowJoinFromOutside ?? (classData.enrollmentMode !== 'manual'),
                        location: classData.location || null,
                        security_question: (classData as any).securityQuestion || null,
                        security_answer: (classData as any).securityAnswer || null,
                        is_locked: (classData as any).isLocked ?? false,
                    });
                    await onUpdateClass({ ...classToEdit, ...classData, allowJoinFromOutside: (classData as any).allowJoinFromOutside, securityQuestion: (classData as any).securityQuestion, isLocked: (classData as any).isLocked }, true);
                } else {
                    const allowJoin = (classData as any).allowJoinFromOutside ?? (classData.enrollmentMode !== 'manual');
                    const created = await apiCreateClass({
                        name: classData.name,
                        academic_year: classData.academicYear,
                        grade_level: (classData.gradeLevel as any),
                        class_type: classData.classType as any,
                        allow_join_from_outside: allowJoin,
                        location: classData.location,
                        security_question: (classData as any).securityQuestion || null,
                        security_answer: (classData as any).securityAnswer || null,
                        is_locked: (classData as any).isLocked ?? false,
                    });
                    const newCls: Class = {
                        id: String(created.id),
                        name: created.name,
                        academicYear: created.academic_year || '',
                        gradeLevel: (classData.gradeLevel as any),
                        studentIds: [],
                        classType: created.class_type as 'physical' | 'virtual' | 'hybrid',
                        allowJoinFromOutside: (created as any).allow_join_from_outside ?? (created.enrollment_mode !== 'manual'),
                        enrollmentMode: (created.enrollment_mode === 'student_code' ? 'manual' : created.enrollment_mode) as 'manual' | 'open' | 'invite_only',
                        location: created.location,
                        inviteCode: created.invite_code,
                        securityQuestion: created.security_question,
                        isLocked: !!created.is_locked,
                    };
                    await onSaveClass(newCls);
                }
                setIsClassModalOpen(false);
            } catch (e: any) {
                alert(e?.message || 'Không thể lưu lớp.');
            } finally {
                setIsSavingClass(false);
            }
            return;
        }
        if (classToEdit) {
            await onUpdateClass({ ...classToEdit, ...classData });
        } else {
            await onSaveClass(classData);
        }
        setIsClassModalOpen(false);
        setIsSavingClass(false);
    };

    const handleSaveStudent = async (studentData: Omit<Student, 'id'>) => {
        if (!studentToEdit && !checkLimit(students.length, limits.maxStudents)) {
            alert(`Bạn đã đạt giới hạn tối đa ${limits.maxStudents} học sinh. Vui lòng nâng cấp gói để tiếp tục.`);
            onNavigate(View.CreditStore);
            return;
        }
        setIsSavingStudent(true);
        try {
            if (studentToEdit) {
                // Logic cập nhật học sinh
                if (APP_MODE === 'PRODUCTION') {
                    await onUpdateStudent({ ...studentToEdit, ...studentData });
                } else {
                    onUpdateStudent({ ...studentToEdit, ...studentData });
                }
                setIsStudentModalOpen(false); // Chỉ đóng khi thành công
            } else {
                // Logic thêm mới học sinh vào lớp - Chỉ gọi 1 API để tránh nhân đôi bản ghi
                if (APP_MODE === 'PRODUCTION' && currentClassIdForStudent) {
                    const result = await addStudentToClassApi(currentClassIdForStudent, {
                        name: studentData.name,
                        real_name: studentData.realName,
                        student_id_code: studentData.studentId,
                        dob: studentData.dob || undefined,
                        parents: studentData.parents
                    });
                    
                    // Chuyển đổi dữ liệu trả về sang định dạng Student của frontend (bao gồm loginCode từ backend)
                    const s = result.student as any;
                    const syncedStudent: Student = {
                        id: String(s.id),
                        name: s.name,
                        realName: s.real_name || s.realName,
                        studentId: s.student_id_code || s.studentId,
                        dob: s.dob,
                        loginCode: s.login_code || s.loginCode,
                        joinedAt: s.joined_at || s.joinedAt,
                        parents: s.parents ? (typeof s.parents === 'string' ? JSON.parse(s.parents) : s.parents) : []
                    };
                    
                    // Sử dụng handleSaveMultipleStudents để cập nhật cả danh sách students và class.studentIds trong 1 lần
                    // giúp UI cập nhật ngay lập tức mà không cần gọi thêm API PATCH class
                    await onSaveMultipleStudents(currentClassIdForStudent, [studentData], [syncedStudent]);
                    
                    setIsStudentModalOpen(false); // Chỉ đóng khi thành công
                } else {
                    const savedStudent = await onSaveStudent(studentData);
                    // Đảm bảo sync cả vào class local nếu ở DEV mode
                    if (currentClassIdForStudent) {
                        const classToUpdate = classes.find(c => c.id === currentClassIdForStudent);
                        if (classToUpdate) {
                            await onUpdateClass({ 
                                ...classToUpdate, 
                                studentIds: Array.from(new Set([...classToUpdate.studentIds, String(savedStudent.id)]))
                            });
                        }
                    }
                    setIsStudentModalOpen(false);
                }
            }
        } catch (e: any) {
            console.error("Lỗi khi lưu học sinh:", e);
            alert(e?.message || 'Không thể lưu học sinh. Vui lòng thử lại.');
        } finally {
            setIsSavingStudent(false);
        }
    };
    
    const handleExportStudents = (classId: string) => {
        const cls = classes.find(c => c.id === classId);
        if (!cls) return;

        const allowJoin = cls.allowJoinFromOutside ?? (cls.enrollmentMode === 'open' || cls.enrollmentMode === 'invite_only');
        const classStudents = allowJoin
            ? Array.from(new Set([
                ...approvedStudents.filter(s => cls.studentIds.map(String).includes(String(s.id))),
                ...cls.studentIds.map(id => students.find(s => String(s.id) === String(id))).filter((s): s is Student => !!s)
              ]))
            : cls.studentIds.map(id => students.find(s => String(s.id) === String(id))).filter((s): s is Student => !!s);

        if (classStudents.length === 0) {
            alert('Lớp học chưa có học sinh để xuất dữ liệu.');
            return;
        }

        // CSV Header
        let csvContent = "\uFEFFHọ tên,Mã số,Ngày sinh,Email,Tên PH1,SĐT PH1,Quan hệ PH1,Tên PH2,SĐT PH2,Quan hệ PH2\n";

        // CSV Rows
        classStudents.forEach(s => {
            const p1 = s.parents?.[0] || { name: '', phoneNumber: '', relationship: '' };
            const p2 = s.parents?.[1] || { name: '', phoneNumber: '', relationship: '' };
            const row = [
                s.name,
                s.studentId || '',
                s.dob || '',
                s.email || '',
                p1.name,
                p1.phoneNumber,
                p1.relationship || '',
                p2.name,
                p2.phoneNumber,
                p2.relationship || ''
            ].map(val => `"${val}"`).join(",");
            csvContent += row + "\n";
        });

        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.setAttribute("href", url);
        link.setAttribute("download", `danh_sach_hoc_sinh_${cls.name.replace(/\s+/g, '_')}.csv`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    };

    const handleCopyLink = (text: string) => {
        navigator.clipboard.writeText(text).then(() => {
            setCopiedLink(text);
            setTimeout(() => setCopiedLink(''), 2000); // Reset after 2 seconds
        });
    };

    const handleRegenerateLoginCode = async (classId: string, student: Student) => {
        if (!confirm('Sinh mã đăng nhập mới? Mã cũ sẽ không còn hiệu lực.')) return;
        setRegeneratingStudentId(String(student.id));
        try {
            if (APP_MODE === 'PRODUCTION') {
                const res = await regenerateStudentLoginCodeApi(classId, student.id);
                onUpdateStudent({ ...student, loginCode: res.login_code }, true);
            } else {
                const newCode = Math.random().toString(36).slice(2, 10).toUpperCase();
                onUpdateStudent({ ...student, loginCode: newCode }, true);
            }
        } catch (e: any) {
            alert(e?.message || 'Không thể sinh mã mới.');
        } finally {
            setRegeneratingStudentId(null);
        }
    };

    const handleCopyStudentLoginInfo = (student: Student, className: string) => {
        const latestStudent = students.find(s => String(s.id) === String(student.id)) || student;
        const dob = latestStudent.dob || (latestStudent as any).date_of_birth || (latestStudent as any).dateOfBirth;
        let dobDigits = '';
        if (dob) {
            const s = String(dob).trim();
            const m = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
            if (m) {
                dobDigits = m[3] + m[2];
            } else {
                try {
                    const d = new Date(dob);
                    const day = String(d.getDate()).padStart(2, '0');
                    const month = String(d.getMonth() + 1).padStart(2, '0');
                    dobDigits = day + month;
                } catch (_) { dobDigits = '____'; }
            }
        } else {
            dobDigits = '____ (chưa có ngày sinh)';
        }
        const name = latestStudent.name + (latestStudent.realName ? ` (${latestStudent.realName})` : '');
        const studentCode = (latestStudent.studentId || (latestStudent as any).student_id_code || '---').toString().toUpperCase();
        const loginCode = (latestStudent.loginCode || (latestStudent as any).login_code || '---').toString().toUpperCase();
        const text = `Thông tin đăng nhập - ${name}\nLớp: ${className}\n\nMã học sinh: ${studentCode}\nMã đăng nhập (8 ký tự): ${loginCode}\nNgày sinh (4 số DDMM): ${dobDigits}\n\nNhập vào form: Mã = ${loginCode}, Ngày sinh = ${dobDigits}\nVí dụ: 1503 = ngày 15 tháng 3`;
        navigator.clipboard.writeText(text).then(() => {
            setCopiedStudentLoginId(String(latestStudent.id));
            setTimeout(() => setCopiedStudentLoginId(''), 2500);
        });
    };

    return (
        <div className="space-y-8">
            <HelpOnboarding steps={teacherHelpSteps} role="teacher" />
            <ClassModal isOpen={isClassModalOpen} onClose={() => setIsClassModalOpen(false)} onSave={handleSaveClass} classToEdit={classToEdit} isSaving={isSavingClass} />
            <StudentModal 
              isOpen={isStudentModalOpen} 
              onClose={() => setIsStudentModalOpen(false)} 
              onSave={handleSaveStudent} 
              studentToEdit={studentToEdit ? (students.find(s => s.id === studentToEdit.id) || studentToEdit) : null}
              isSaving={isSavingStudent}
              currentClassId={currentClassIdForStudent}
              currentClassName={currentClassIdForStudent ? classes.find(c => c.id === currentClassIdForStudent)?.name : undefined}
              onRegenerateLoginCode={studentToEdit && currentClassIdForStudent ? () => handleRegenerateLoginCode(currentClassIdForStudent, students.find(s => s.id === studentToEdit.id) || studentToEdit) : undefined}
              onCopyLoginInfo={studentToEdit ? () => handleCopyStudentLoginInfo(students.find(s => s.id === studentToEdit.id) || studentToEdit, classes.find(c => c.id === currentClassIdForStudent)?.name || '') : undefined}
              isRegeneratingCode={!!regeneratingStudentId}
              copiedLoginId={copiedStudentLoginId}
            />
            {studentForReport && (
                <StudentReportModal 
                    isOpen={isReportModalOpen} 
                    onClose={() => setIsReportModalOpen(false)} 
                    student={studentForReport}
                    homeworks={homeworks}
                    submissions={props.submissions || []}
                    assignments={props.assignments || []}
                />
            )}
            <AddMultipleStudentsModal 
                isOpen={isMultiStudentModalOpen} 
                onClose={() => setIsMultiStudentModalOpen(false)} 
                isSaving={isSavingStudent} 
                existingStudents={students}
                onSave={async (studentList) => {
                if(currentClassIdForStudent) {
                    if (!checkLimit(students.length + studentList.length, limits.maxStudents)) {
                        alert(`Dữ liệu này sẽ vượt quá giới hạn ${limits.maxStudents} học sinh của bạn (Hiện có: ${students.length}, thêm: ${studentList.length}). Vui lòng nâng cấp gói để tiếp tục.`);
                        onNavigate(View.CreditStore);
                        return;
                    }
                    setIsSavingStudent(true);
                    if (APP_MODE === 'PRODUCTION') {
                        try { 
                            // In production, we send the full list with parent info to the API
                            const payload = studentList.map(s => ({
                                name: s.name,
                                student_id_code: s.studentId,
                                dob: s.dob,
                                email: s.email,
                                parent_name: s.parent_name,
                                parent_phone: s.parent_phone,
                                parent_relationship: s.parent_relationship,
                                parent_name_2: s.parent_name_2,
                                parent_phone_2: s.parent_phone_2,
                                parent_relationship_2: s.parent_relationship_2
                            }));
                            
                            const result = await addMultipleStudentsApi(currentClassIdForStudent, payload); 
                            
                            const syncedStudents: Student[] = result.students.map((s: any) => ({
                                id: String(s.id),
                                name: s.name,
                                realName: s.real_name || s.realName,
                                studentId: s.student_id_code || s.studentId,
                                dob: s.dob,
                                loginCode: s.login_code || s.loginCode,
                                joinedAt: s.joined_at || s.joinedAt,
                                parents: s.parents ? (typeof s.parents === 'string' ? JSON.parse(s.parents) : s.parents) : []
                            }));
                            
                            await onSaveMultipleStudents(currentClassIdForStudent, studentList, syncedStudents);
                            
                            // Also update the class object to include the new students on the server
                            const cls = classes.find(c => c.id === currentClassIdForStudent);
                            if (cls) {
                                const newIds = syncedStudents.map(s => String(s.id));
                                const updatedClass = { 
                                    ...cls, 
                                    studentIds: Array.from(new Set([...cls.studentIds, ...newIds])) 
                                };
                                // onSaveMultipleStudents in App.tsx already updates the local classes state, so we don't need this call.
                                // await onUpdateClass(updatedClass);
                            }
                            
                            setIsMultiStudentModalOpen(false);
                        } catch (e: any) { 
                            console.error("Bulk import error:", e);
                            let msg = 'Không thể thêm danh sách học sinh.';
                            if (e?.message?.includes('Duplicate entry') || e?.message?.includes('1062') || e?.message?.includes('23505') || e?.message?.includes('duplicate key')) {
                                msg = 'Một hoặc nhiều học sinh đã tồn tại (Mã HS hoặc Email bị trùng). Hệ thống sẽ tự tạo mã mới nếu trùng - vui lòng thử lại.';
                            }
                            alert(msg); 
                        } finally {
                            setIsSavingStudent(false);
                        }
                    } else {
                        await onSaveMultipleStudents(currentClassIdForStudent, studentList);
                        setIsMultiStudentModalOpen(false);
                        setIsSavingStudent(false);
                    }
                }
            }} />
            <AssignHomeworkModal 
                isOpen={isAssignModalOpen} 
                onClose={() => { setIsAssignModalOpen(false); setEditingAssignment(null); }} 
                homeworks={homeworks} 
                classStudents={classToAssign ? (classes.find(c => c.id === classToAssign)?.classType === 'online' ? approvedStudents : (classes.find(c => c.id === classToAssign)?.studentIds.map(id => students.find(s => String(s.id) === String(id))).filter((s): s is Student => !!s) || [])) : []}
                classes={classes}
                classToAssign={classToAssign}
                isSaving={isSavingAssignment} 
                initialType={assignModalType}
                editingAssignment={editingAssignment}
                onNavigate={onNavigate}
                user={user}
                onAssign={async (assignmentData) => {
                    if (classToAssign) {
                        setIsSavingAssignment(true);
                        try {
                            // Gọi handleAssignHomework từ App.tsx (đã hỗ trợ cả update và post)
                            await onAssignHomework(assignmentData);
                            setIsAssignModalOpen(false);
                            setEditingAssignment(null);
                        } catch (error) {
                            console.error("Lỗi khi lưu bài tập:", error);
                        } finally {
                            setIsSavingAssignment(false);
                        }
                    }
                }} 
            />

            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-6">
                <div>
                    <h2 className="text-3xl font-bold text-gray-900 tracking-tight">
                        {props.activeClass ? `Lớp ${props.activeClass.name}` : 'Quản lý Lớp học'}
                    </h2>
                </div>
                
                <div className="flex flex-wrap gap-2">
                    <button onClick={() => { setClassToEdit(null); setIsClassModalOpen(true); }} disabled={isSavingClass} className="flex items-center gap-2 px-6 py-2 bg-teal-600 text-white font-bold rounded-xl hover:bg-teal-700 shadow-lg shadow-teal-100 active:scale-95 disabled:opacity-50 transition-all text-sm">
                        {isSavingClass ? <Spinner className="h-4 w-4" /> : <PlusIcon className="h-4 w-4" />}
                        <span>Tạo lớp mới</span>
                    </button>
                </div>
            </div>

            {(isLoading && classes.length === 0) ? (
                <div className="space-y-6">
                    <ClassSkeleton />
                    <ClassSkeleton />
                    <ClassSkeleton />
                </div>
            ) : filteredClasses.length === 0 ? (
                <p className="text-center text-gray-500 py-10">Chưa có lớp học nào. Hãy tạo một lớp mới để bắt đầu.</p>
            ) : (
                <div className="space-y-6">
                    {filteredClasses.map(cls => {
                        const allowJoin = cls.allowJoinFromOutside ?? (cls.enrollmentMode === 'open' || cls.enrollmentMode === 'invite_only');
                        // Khi allowJoin: kết hợp approved + manual. Khi không: chỉ từ studentIds
                        const classStudents = allowJoin
                            ? Array.from(new Set([
                                ...approvedStudents.filter(s => cls.studentIds.map(String).includes(String(s.id))),
                                ...cls.studentIds.map(id => students.find(s => String(s.id) === String(id))).filter((s): s is Student => !!s)
                              ]))
                            : cls.studentIds.map(id => students.find(s => String(s.id) === String(id))).filter((s): s is Student => !!s);
                        
                        const classPendingRequests = pendingRequests.filter(r => String(r.classId || r.class_id) === String(cls.id));
                        const classTasks = assignments.filter(a => a.classId === cls.id && (a.status !== 'recalled') && (a.type === 'task' || (a as any).assignment_type === 'task'));
                        const classAnnouncements = assignments.filter(a => a.classId === cls.id && (a.status !== 'recalled') && (a.type === 'announcement' || (a as any).assignment_type === 'announcement'));
                        const classHomeworks = assignments.filter(a => a.classId === cls.id && (a.status !== 'recalled') && (a.type === 'homework' || (a as any).assignment_type === 'homework' || (a.homeworkId && !a.type)));
                        const tabs = [
                            { id: 'requests', label: 'Yêu cầu chờ', count: classPendingRequests.length, color: 'text-amber-600' },
                            { id: 'students', label: 'Học sinh', count: classStudents.length, color: 'text-teal-600' },
                            { id: 'parents', label: 'Phụ huynh', count: classStudents.reduce((acc, s) => acc + (Array.isArray(s.parents) ? s.parents.length : 0), 0), color: 'text-blue-600' },
                            { id: 'sessions', label: 'Buổi học', count: cls.sessions?.length || 0, color: 'text-orange-600' },
                            { id: 'homework', label: 'Bài tập', count: classHomeworks.length, color: 'text-sky-600' },
                            { id: 'tasks', label: 'Nhiệm vụ', count: classTasks.length, color: 'text-teal-600' },
                            { id: 'announcements', label: 'Thông báo', count: classAnnouncements.length, color: 'text-amber-600' },
                            { id: 'reports', label: 'Báo cáo', color: 'text-purple-600' },
                        ];

                        return (
                        <Card key={cls.id}>
                            <div className="p-4 border-b flex justify-between items-center bg-gray-50">
                                <div className="flex items-center gap-3">
                                    <UserGroupIcon className="h-6 w-6 text-teal-600" />
                                    <div>
                                        <h3 className="text-lg font-bold text-gray-800">{cls.name}</h3>
                                        <div className="flex flex-wrap items-center gap-2">
                                            <p className="text-xs text-gray-500">{cls.academicYear} | {cls.gradeLevel}</p>
                                            <span className={`px-2 py-0.5 text-[10px] font-black uppercase rounded-full ${
                                                cls.classType === 'virtual' ? 'bg-blue-100 text-blue-800' : 
                                                cls.classType === 'physical' ? 'bg-green-100 text-green-800' : 'bg-purple-100 text-purple-800'
                                            }`}>
                                                {cls.classType === 'virtual' ? 'Trực tuyến' : cls.classType === 'physical' ? 'Tại chỗ' : 'Hybrid'}
                                            </span>
                                            <span className={`px-2 py-0.5 text-[10px] font-black uppercase rounded-full ${
                                                allowJoin ? 'bg-sky-100 text-sky-800' : 'bg-gray-100 text-gray-800'
                                            }`}>
                                                {allowJoin ? 'Cho phép join' : 'Chỉ GV thêm'}
                                            </span>
                                            {cls.isLocked && (
                                                <span className="px-2 py-0.5 text-[10px] font-black uppercase rounded-full bg-rose-100 text-rose-800" title="Lớp đang khóa - HS không thể đăng nhập mã">Đã khóa</span>
                                            )}
                                        </div>
                                    </div>
                                </div>
                                <div className="flex items-center gap-2">
                                     <div className="hidden sm:flex items-center gap-1.5 mr-2">
                                        <button 
                                            onClick={() => { setClassToAssign(cls.id); setAssignModalType('homework'); setIsAssignModalOpen(true); }} 
                                            className="px-3 py-1.5 text-[10px] font-black uppercase tracking-wider text-white bg-blue-600 rounded-lg hover:bg-blue-700 shadow-sm transition-all active:scale-95 flex items-center gap-1.5"
                                        >
                                            <ClipboardDocumentListIcon className="h-3.5 w-3.5" />
                                            Giao bài tập
                                        </button>
                                        <button 
                                            onClick={() => { setClassToAssign(cls.id); setAssignModalType('task'); setIsAssignModalOpen(true); }} 
                                            className="px-3 py-1.5 text-[10px] font-black uppercase tracking-wider text-white bg-teal-600 rounded-lg hover:bg-teal-700 shadow-sm transition-all active:scale-95 flex items-center gap-1.5"
                                        >
                                            <ClipboardDocumentCheckIcon className="h-3.5 w-3.5" />
                                            Nhiệm vụ
                                        </button>
                                        <button 
                                            onClick={() => { setClassToAssign(cls.id); setAssignModalType('announcement'); setIsAssignModalOpen(true); }} 
                                            className="px-3 py-1.5 text-[10px] font-black uppercase tracking-wider text-white bg-sky-500 rounded-lg hover:bg-sky-600 shadow-sm transition-all active:scale-95 flex items-center gap-1.5"
                                        >
                                            <MegaphoneIconSolid className="h-3.5 w-3.5" />
                                            Thông báo
                                        </button>
                                     </div>
                                     <div className="sm:hidden">
                                        <button onClick={() => { setClassToAssign(cls.id); setAssignModalType('homework'); setIsAssignModalOpen(true); }} className="p-2 bg-blue-600 text-white rounded-lg"><PlusIcon className="h-4 w-4" /></button>
                                     </div>
                                     <button onClick={() => { setClassToEdit(cls); setIsClassModalOpen(true); }} className="p-2 hover:bg-gray-200 rounded-full" title="Sửa lớp"><PencilIcon className="h-4 w-4 text-gray-600" /></button>
                                     <button onClick={async () => { if(confirm(`Xóa lớp ${cls.name}?`)) { if (APP_MODE === 'PRODUCTION') { try { await apiDeleteClass(cls.id); } catch (e: any) { alert(e?.message || 'Không thể xóa lớp.'); return; } } onDeleteClass(cls.id, true); }} } className="p-2 hover:bg-gray-200 rounded-full" title="Xóa lớp"><TrashIcon className="h-4 w-4 text-red-500" /></button>
                                </div>
                            </div>

                            <div className="flex flex-col h-full">
                                <div className="p-4 bg-slate-50 border-b border-gray-100 flex flex-col md:flex-row gap-4">
                                    <div className="flex-1 p-3 bg-white border border-gray-200 rounded-xl shadow-sm">
                                        <div className="flex justify-between items-center mb-1">
                                            <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Mã lớp (định danh 6 ký tự)</label>
                                            <button onClick={() => handleCopyLink(cls.inviteCode || '')} className="text-teal-600 hover:text-teal-700 text-xs font-bold">
                                                {copiedLink === cls.inviteCode ? 'Đã chép!' : 'Sao chép'}
                                            </button>
                                        </div>
                                        <p className="font-mono text-xl font-black text-slate-800 tracking-[0.2em]">{cls.inviteCode || '---'}</p>
                                        <p className="text-[10px] text-slate-500 mt-1">HS đăng nhập bằng mã 8 ký tự + ngày sinh (xem tab Học sinh)</p>
                                    </div>
                                    {allowJoin && (
                                        <div className="flex-[2] p-3 bg-white border border-gray-200 rounded-xl shadow-sm space-y-2">
                                            <div className="flex justify-between items-center mb-1">
                                                <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Link tham gia lớp học</label>
                                                <button onClick={() => handleCopyLink(`${window.location.origin}/tham-gia-lop-hoc?code=${cls.inviteCode}`)} className="text-teal-600 hover:text-teal-700 text-xs font-bold">
                                                    {copiedLink === `${window.location.origin}/tham-gia-lop-hoc?code=${cls.inviteCode}` ? 'Đã chép!' : 'Sao chép link'}
                                                </button>
                                            </div>
                                            <div className="flex items-center gap-2">
                                                <input type="text" readOnly value={`${window.location.origin}/tham-gia-lop-hoc?code=${cls.inviteCode}`} className="flex-grow text-xs p-0 bg-transparent border-none focus:ring-0 text-slate-500 truncate"/>
                                            </div>
                                            {cls.enrollmentMode === 'invite_only' && cls.inviteExpiryDate && (
                                                <p className="text-[10px] text-amber-600 font-medium">Thời hạn gia nhập: {new Date(cls.inviteExpiryDate).toLocaleString('vi-VN')}</p>
                                            )}
                                        </div>
                                    )}
                                </div>

                                <div className="border-b border-gray-100 px-4 flex gap-6 overflow-x-auto scrollbar-hide">
                                    {tabs.map(tab => (
                                        <button 
                                            key={tab.id}
                                            onClick={() => setActiveTab(cls.id, tab.id)}
                                            className={`py-3 text-xs font-black uppercase tracking-widest relative whitespace-nowrap transition-all ${
                                                getActiveTab(cls.id) === tab.id ? `${tab.color} border-b-2 border-current` : 'text-gray-400 hover:text-gray-600'
                                            }`}
                                        >
                                            <div className="flex items-center gap-2">
                                                {tab.label}
                                                {tab.count !== undefined && (
                                                    <span className={`px-1.5 py-0.5 rounded-full text-[10px] bg-gray-100 text-gray-500`}>
                                                        {tab.count}
                                                    </span>
                                                )}
                                            </div>
                                        </button>
                                    ))}
                                </div>

                                <div className="p-4 min-h-[300px]">
                                    {isLoading && classStudents.length === 0 && (getActiveTab(cls.id) === 'students' || getActiveTab(cls.id) === 'parents') && (
                                        <div className="space-y-4 animate-pulse">
                                            {[1, 2, 3, 4].map(i => (
                                                <div key={i} className="h-16 bg-slate-50 rounded-2xl border border-slate-100"></div>
                                            ))}
                                        </div>
                                    )}

                                    {isLoading && classHomeworks.length === 0 && getActiveTab(cls.id) === 'homework' && (
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 animate-pulse">
                                            {[1, 2, 3, 4].map(i => (
                                                <div key={i} className="h-24 bg-slate-50 rounded-2xl border border-slate-100"></div>
                                            ))}
                                        </div>
                                    )}

                                    {getActiveTab(cls.id) === 'requests' && (
                                        <div className="animate-in fade-in slide-in-from-top-1 duration-300">
                                            {isLoading && (
                                                <div className="p-4">
                                                    <TabSkeleton />
                                                </div>
                                            )}
                                            {/* ... requests logic ... */}
                                            <div className="flex justify-between items-center mb-4">
                                                <h4 className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Duyệt yêu cầu tham gia & Kết nối</h4>
                                                <button 
                                                    disabled={refreshCooldown > 0 || isRefreshingRequests}
                                                    onClick={handleRefreshManual}
                                                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[10px] font-black uppercase tracking-wider transition-all active:scale-95 ${
                                                        refreshCooldown > 0 || isRefreshingRequests
                                                        ? 'bg-gray-100 text-gray-400 cursor-not-allowed' 
                                                        : 'bg-teal-50 text-teal-600 hover:bg-teal-100'
                                                    }`}
                                                >
                                                    <ArrowPathIcon className={`h-3.5 w-3.5 ${isRefreshingRequests ? 'animate-spin' : ''}`} />
                                                    {isRefreshingRequests ? 'Đang tải...' : refreshCooldown > 0 ? `Chờ ${refreshCooldown}s` : 'Tải mới'}
                                                </button>
                                            </div>
                                            
                                            <>
                                            {/* Phần yêu cầu từ Phụ huynh */}
                                            <div className="mb-8">
                                                <h5 className="text-[9px] font-black text-amber-600 uppercase tracking-[0.2em] mb-3">Yêu cầu kết nối Phụ huynh</h5>
                                                {classPendingRequests.length > 0 ? (
                                                    <ul className="space-y-3">
                                                        {classPendingRequests.map(req => (
                                                            <li key={req.id} className="p-4 bg-white rounded-2xl border border-amber-100 shadow-sm flex justify-between items-center hover:shadow-md transition-all">
                                                                <div className="flex items-center gap-4">
                                                                    <div className="w-12 h-12 bg-amber-50 text-amber-600 rounded-2xl flex items-center justify-center font-black border border-amber-100">
                                                                        {req.parentName.charAt(0)}
                                                                    </div>
                                                                    <div>
                                                                        <div className="flex items-center gap-2">
                                                                            <span className="font-black text-slate-800">{req.parentName}</span>
                                                                            <span className="px-2 py-0.5 bg-amber-50 text-[8px] font-black text-amber-600 rounded-full uppercase">Phụ huynh</span>
                                                                        </div>
                                                                        <p className="text-[10px] text-slate-500 font-bold">SĐT: {req.parentPhone || 'Chưa cập nhật'}</p>
                                                                        <div className="mt-1 flex flex-col gap-1">
                                                                            <div className="flex items-center gap-1.5 flex-wrap">
                                                                                <span className="text-[10px] text-slate-400 font-medium">Kết nối:</span>
                                                                                <span className="px-2 py-0.5 bg-sky-50 text-sky-700 rounded-lg text-[10px] font-black border border-sky-100">{req.studentName || req.childName || 'Học sinh mới'}</span>
                                                                                {((req.studentId || req.student_id) && (() => {
                                                                                    const linkedStudent = classStudents.find(s => String(s.id) === String(req.studentId || req.student_id));
                                                                                    return linkedStudent ? <span className="px-2 py-0.5 bg-amber-50 text-amber-700 rounded-lg text-[10px] font-bold border border-amber-100">Mã HS: {linkedStudent.studentId || (linkedStudent as any).student_id_code || '---'}</span> : null;
                                                                                })())}
                                                                            </div>
                                                                            {req.message && (
                                                                                <div className="mt-1 p-2 bg-slate-50 rounded-lg border border-slate-100 italic text-[10px] text-slate-600 leading-relaxed">
                                                                                    <span className="font-bold not-italic text-slate-400 uppercase text-[8px] block mb-1">Ghi chú xác nhận:</span>
                                                                                    {req.message}
                                                                                </div>
                                                                            )}
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                                <div className="flex gap-2">
                                                                    <button 
                                                                        disabled={!!processingRequestId}
                                                                        onClick={() => handleOpenRejectModal(req.id)} 
                                                                        className="px-3 py-2 text-[10px] font-black text-slate-400 uppercase hover:text-rose-500 transition-colors disabled:opacity-50"
                                                                    >
                                                                        Từ chối
                                                                    </button>
                                                                    <button 
                                                                        disabled={!!processingRequestId}
                                                                        onClick={() => handleApproveRequest(req.id)} 
                                                                        className="flex items-center gap-2 px-5 py-2 bg-teal-600 text-white text-[10px] font-black uppercase tracking-widest rounded-xl hover:bg-teal-700 shadow-lg shadow-teal-100 active:scale-95 transition-all disabled:opacity-50"
                                                                    >
                                                                        {processingRequestId === req.id && <Spinner className="h-3 w-3" />}
                                                                        <span>Phê duyệt</span>
                                                                    </button>
                                                                </div>
                                                            </li>
                                                        ))}
                                                    </ul>
                                                ) : (
                                                    <p className="text-xs text-slate-400 italic py-4 text-center bg-slate-50 rounded-xl border border-dashed">Không có yêu cầu kết nối phụ huynh.</p>
                                                )}
                                            </div>

                                            {/* Phần yêu cầu từ Học sinh (cho tất cả lớp có mã join) */}
                                            {cls.inviteCode && (
                                                <div>
                                                    <h5 className="text-[9px] font-black text-sky-600 uppercase tracking-[0.2em] mb-3">Yêu cầu tham gia lớp (Học sinh)</h5>
                                                    {pendingStudents.length > 0 ? (
                                                        <ul className="space-y-3">
                                                            {pendingStudents.map(student => (
                                                                <li key={student.id} className="p-4 bg-white rounded-2xl border border-sky-100 shadow-sm flex justify-between items-center">
                                                                    <div className="flex items-center gap-3">
                                                                        <div className="w-10 h-10 bg-sky-50 text-sky-600 rounded-full flex items-center justify-center font-bold border border-sky-100">{student.name.charAt(0)}</div>
                                                                            <div>
                                                                            <p className="text-sm font-bold text-gray-800">{student.name}</p>
                                                                            <p className="text-[10px] text-gray-400">{student.email || 'Học sinh tự do'}</p>
                                                                        </div>
                                                                    </div>
                                                                    <div className="flex gap-2">
                                                                        <button onClick={() => handleDecline(String(student.id))} className="p-2 text-slate-300 hover:text-rose-500 transition-colors"><XMarkIcon className="h-5 w-5"/></button>
                                                                        <button onClick={() => handleAccept(String(student.id))} className="p-2 text-teal-600 hover:bg-teal-50 rounded-lg transition-all"><CheckIcon className="h-5 w-5"/></button>
                                                                    </div>
                                                                </li>
                                                            ))}
                                                        </ul>
                                                    ) : (
                                                        <p className="text-xs text-slate-400 italic py-4 text-center bg-slate-50 rounded-xl border border-dashed">Không có yêu cầu tham gia từ học sinh.</p>
                                                    )}
                                                </div>
                                            )}
                                            </>
                                        </div>
                                    )}

                                    {getActiveTab(cls.id) === 'students' && (
                                        <div className="animate-in fade-in slide-in-from-top-1 duration-300">
                                            <div className="flex justify-between items-center mb-4">
                                                <h4 className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Danh sách học sinh</h4>
                                                <div className="flex gap-4 items-center">
                                                    <button 
                                                        onClick={() => handleExportStudents(cls.id)}
                                                        className="text-xs font-bold text-slate-500 hover:text-teal-600 flex items-center gap-1 transition-colors"
                                                    >
                                                        <ArrowPathIcon className="h-3.5 w-3.5 rotate-180" /> Xuất Excel
                                                    </button>
                                                    <button onClick={() => { setCurrentClassIdForStudent(cls.id); setIsMultiStudentModalOpen(true); }} className="text-xs font-bold text-teal-600 hover:underline">Thêm nhiều học sinh</button>
                                                    <button onClick={() => { setCurrentClassIdForStudent(cls.id); setStudentToEdit(null); setIsStudentModalOpen(true); }} className="text-xs font-bold text-teal-600 hover:underline">+ Thêm học sinh thủ công</button>
                                                </div>
                                            </div>
                                            {classStudents.length > 0 ? (
                                                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                                    {classStudents.map(student => (
                                                        <div key={student.id} className="p-3 bg-white border border-gray-100 rounded-2xl shadow-sm flex justify-between items-center group hover:border-teal-200 transition-all">
                                                            <div className="flex items-center gap-3">
                                                                <div className="w-10 h-10 bg-teal-50 text-teal-600 rounded-full flex items-center justify-center font-black">{student.name.charAt(0)}</div>
                                                                <div>
                                                                    <p className="text-sm font-bold text-gray-800">
                                                                        {student.name}
                                                                        {student.realName && (
                                                                            <span className="ml-2 text-[10px] text-slate-400 font-medium italic">({student.realName})</span>
                                                                        )}
                                                                    </p>
                                                                    <div className="flex flex-center gap-2 mt-0.5 flex-wrap items-center">
                                                                        {/* 1. Hiển thị tài khoản đã liên kết (Real User Accounts) */}
                                                                        <div className="flex flex-wrap gap-1 items-center">
                                                                            {Array.isArray(student.parents) && student.parents.filter((p: any) => p.id && !String(p.id).startsWith('contact_')).map((p: any) => (
                                                                                <span key={p.id} className="text-[9px] font-black text-white bg-teal-600 px-2 py-0.5 rounded-full flex items-center gap-1 shadow-sm border border-teal-700" title={`Tài khoản PH: ${p.name} - Đã kích hoạt hệ thống`}>
                                                                                    <CheckIcon className="h-2 w-2 stroke-[4px]" />
                                                                                    PH: {p.name}
                                                                                </span>
                                                                            ))}
                                                                            {student.studentAccount && (
                                                                                <span className="text-[9px] font-black text-white bg-sky-600 px-2 py-0.5 rounded-full flex items-center gap-1 shadow-sm border border-sky-700" title={`Tài khoản HS: ${student.studentAccount.name} - Đã kích hoạt hệ thống`}>
                                                                                    <CheckIcon className="h-2 w-2 stroke-[4px]" />
                                                                                    HS: {student.studentAccount.name}
                                                                                </span>
                                                                            )}
                                                                        </div>

                                                                        {/* 2. Hiển thị thông tin phụ huynh (Contact only) */}
                                                                        {Array.isArray(student.parents) && student.parents.filter((p: any) => !p.id || String(p.id).startsWith('contact_')).map((p: any) => (
                                                                            <span key={p.id || p.name} className="text-[9px] font-bold text-slate-500 bg-slate-100 px-1.5 py-0.5 rounded border border-slate-200 flex items-center gap-1" title={`${p.name} (Thông tin liên hệ - Chưa kết nối tài khoản)`}>
                                                                                <UserGroupIcon className="h-2 w-2" />
                                                                                {p.name}
                                                                            </span>
                                                                        ))}

                                                                        {/* 3. Nút Link kết nối - Luôn hiển thị nếu được phép */}
                                                                        {allowJoin && cls.inviteCode && !String(student.id).startsWith('approved_') && (
                                                                            <button
                                                                                onClick={(e) => { e.stopPropagation(); handleCopyLink(`${window.location.origin}/tham-gia-lop-hoc?code=${cls.inviteCode}&student=${student.id}`); }}
                                                                                className={`inline-flex items-center gap-1 text-[9px] font-bold transition-all px-1.5 py-0.5 rounded border ${
                                                                                    (Array.isArray(student.parents) && student.parents.length > 0) 
                                                                                    ? 'text-slate-400 hover:text-sky-600 bg-white hover:bg-sky-50 border-slate-100 hover:border-sky-200' 
                                                                                    : 'text-sky-600 hover:text-sky-700 bg-sky-50 hover:bg-sky-100 border-sky-100'
                                                                                }`}
                                                                                title="Sao chép link kết nối riêng cho học sinh này - gửi cho phụ huynh"
                                                                            >
                                                                                {copiedLink === `${window.location.origin}/tham-gia-lop-hoc?code=${cls.inviteCode}&student=${student.id}` ? (
                                                                                    <>Đã chép!</>
                                                                                ) : (
                                                                                    <><LinkIcon className="h-3 w-3" /> Link kết nối</>
                                                                                )}
                                                                            </button>
                                                                        )}

                                                                        {(!Array.isArray(student.parents) || student.parents.length === 0) && !student.studentAccount && (
                                                                            <span className="text-[9px] text-rose-400 font-bold animate-pulse">Chưa kết nối PH</span>
                                                                        )}
                                                                        <span className="text-[8px] text-slate-300">•</span>
                                                                        <p className="text-[10px] text-gray-400 font-medium">{student.studentId || 'Chưa có mã HS'}</p>
                                                                        {student.joinedAt && (
                                                                            <span className="text-[9px] text-slate-400">• {new Date(student.joinedAt).toLocaleDateString('vi-VN')}</span>
                                                                        )}
                                                                        <span className="text-[8px] text-slate-300">•</span>
                                                                        <div className="flex items-center gap-1 bg-amber-50 px-1.5 py-0.5 rounded border border-amber-100 shadow-sm group/code" title="Mã đăng nhập cho học sinh">
                                                                            <span className="text-[9px] font-black text-amber-600 uppercase tracking-tighter">
                                                                                {student.loginCode 
                                                                                    ? (visibleLoginCodes[student.id] ? `Mã ĐN: ${student.loginCode}` : 'Mã ĐN: ********') 
                                                                                    : 'Chưa có mã ĐN'}
                                                                            </span>
                                                                            {student.loginCode ? (
                                                                                <>
                                                                                    <button 
                                                                                        onClick={(e) => { e.stopPropagation(); toggleLoginCodeVisibility(String(student.id)); }}
                                                                                        className="p-0.5 hover:bg-amber-200 rounded transition-all text-amber-700"
                                                                                        title={visibleLoginCodes[student.id] ? "Ẩn mã" : "Xem mã"}
                                                                                    >
                                                                                        {visibleLoginCodes[student.id] ? <EyeSlashIcon className="h-3 w-3" /> : <EyeIcon className="h-3 w-3" />}
                                                                                    </button>
                                                                                    <button 
                                                                                        onClick={(e) => { e.stopPropagation(); handleCopyStudentLoginInfo(student, cls.name); }}
                                                                                        className="p-0.5 hover:bg-amber-200 rounded transition-all"
                                                                                        title="Sao chép thông tin đăng nhập"
                                                                                    >
                                                                                        {copiedStudentLoginId === String(student.id) ? (
                                                                                            <span className="text-[8px] font-bold text-teal-600">Đã chép!</span>
                                                                                        ) : (
                                                                                            <ClipboardDocumentListIcon className="h-3 w-3 text-amber-700" />
                                                                                        )}
                                                                                    </button>
                                                                                    <button 
                                                                                        onClick={(e) => { e.stopPropagation(); handleRegenerateLoginCode(cls.id, student); }}
                                                                                        disabled={!!regeneratingStudentId}
                                                                                        className="p-0.5 hover:bg-amber-200 rounded transition-all"
                                                                                        title="Sinh mã đăng nhập mới"
                                                                                    >
                                                                                        {regeneratingStudentId === student.id ? (
                                                                                            <Spinner className="h-3 w-3 text-amber-700" />
                                                                                        ) : (
                                                                                            <ArrowPathIcon className="h-3 w-3 text-amber-700" />
                                                                                        )}
                                                                                    </button>
                                                                                </>
                                                                            ) : (
                                                                                <button 
                                                                                    onClick={(e) => { e.stopPropagation(); handleRegenerateLoginCode(cls.id, student); }}
                                                                                    disabled={!!regeneratingStudentId}
                                                                                    className="p-0.5 hover:bg-amber-200 rounded transition-all"
                                                                                    title="Tạo mã đăng nhập mới"
                                                                                >
                                                                                    {regeneratingStudentId === student.id ? (
                                                                                        <Spinner className="h-3 w-3 text-amber-700" />
                                                                                    ) : (
                                                                                        <PlusIcon className="h-3 w-3 text-amber-700" />
                                                                                    )}
                                                                                </button>
                                                                            )}
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                            <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                                                <button onClick={() => { setStudentForReport(student); setIsReportModalOpen(true); }} className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg" title="Xem báo cáo"><ChartBarIcon className="h-4 w-4"/></button>
                                                                {(student as any).user_id && APP_MODE === 'PRODUCTION' && (
                                                                    <button
                                                                        onClick={async () => {
                                                                            if (!confirm(`Hủy liên kết tài khoản học sinh với hồ sơ "${student.name}"? Học sinh có thể liên kết lại với tài khoản khác.`)) return;
                                                                            const r = await unlinkStudentAccountApi(cls.id, student.id);
                                                                            if (r.success) await onRefreshClassUpdates?.(cls.id);
                                                                            else alert(r.message || 'Không thể hủy liên kết');
                                                                        }}
                                                                        className="p-2 text-amber-600 hover:bg-amber-50 rounded-lg"
                                                                        title="Hủy liên kết tài khoản (liên kết nhầm)"
                                                                    >
                                                                        <XMarkIcon className="h-4 w-4" />
                                                                    </button>
                                                                )}
                                                                <button onClick={() => { setCurrentClassIdForStudent(cls.id); setStudentToEdit(student); setIsStudentModalOpen(true); }} className="p-2 text-gray-400 hover:bg-gray-100 rounded-lg"><PencilIcon className="h-4 w-4"/></button>
                                                                <button onClick={() => (String(student.id).startsWith('approved_') ? handleRemoveOnlineMember(String(student.id)) : handleRemoveStudentFromOfflineClass(cls.id, String(student.id)))} className="p-2 text-red-400 hover:bg-red-50 rounded-lg"><TrashIcon className="h-4 w-4"/></button>
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                            ) : (
                                                <div className="text-center py-12 bg-gray-50/50 rounded-2xl border-2 border-dashed">
                                                    <p className="text-sm text-gray-400 italic">Chưa có thành viên nào trong lớp này.</p>
                                                </div>
                                            )}
                                        </div>
                                    )}

                                    {getActiveTab(cls.id) === 'parents' && (
                                        <div className="animate-in fade-in slide-in-from-top-1 duration-300">
                                            <h4 className="text-xs font-semibold text-slate-500 uppercase tracking-widest mb-4">Thông tin liên hệ phụ huynh</h4>
                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                                {classStudents.filter(s => Array.isArray(s.parents) && s.parents.length > 0).map(student => (
                                                    <Card key={student.id} className="p-4 border-slate-100 bg-slate-50/30">
                                                        <p className="text-xs font-black text-gray-400 mb-2 border-b pb-1">
                                                            Học sinh: {student.name}
                                                            {student.realName && (
                                                                <span className="ml-1 opacity-70 italic font-medium">({student.realName})</span>
                                                            )}
                                                        </p>
                                                        <div className="space-y-3">
                                                            {Array.isArray(student.parents) && student.parents.map(parent => {
                                                                const parentUserId = typeof parent.id === 'number' ? parent.id : (/^\d+$/.test(String(parent.id || '')) ? parseInt(String(parent.id), 10) : null);
                                                                const isLinkedParent = parentUserId != null && !String(parent.id || '').startsWith('contact_');
                                                                return (
                                                                <div key={parent.id} className="flex justify-between items-center gap-2">
                                                                    <div className="min-w-0">
                                                                        <div className="flex items-center gap-2">
                                                                            <p className="text-sm font-bold text-gray-800">
                                                                                {parent.name}
                                                                                {parent.shortName && parent.shortName !== parent.name && (
                                                                                    <span className="ml-1 text-[10px] text-slate-400 font-medium italic">({parent.shortName})</span>
                                                                                )}
                                                                            </p>
                                                                            {isLinkedParent && (
                                                                                <span className="text-[8px] font-black text-white bg-teal-500 px-1.5 py-0.5 rounded-full uppercase tracking-widest shadow-sm">Đã kích hoạt</span>
                                                                            )}
                                                                        </div>
                                                                        <p className="text-[10px] text-teal-600 font-bold">{parent.relationship || 'PH'}</p>
                                                                    </div>
                                                                    <div className="flex items-center gap-1 shrink-0">
                                                                        {parent.phoneNumber && (
                                                                            <a href={`tel:${parent.phoneNumber}`} className="flex items-center gap-1.5 px-2.5 py-1 bg-white border border-gray-200 rounded-lg text-xs font-bold text-gray-600 hover:bg-teal-50 hover:text-teal-600 hover:border-teal-200 transition-all">
                                                                                <PhoneIcon className="h-3 w-3" /> {parent.phoneNumber}
                                                                            </a>
                                                                        )}
                                                                        {isLinkedParent && APP_MODE === 'PRODUCTION' && (
                                                                            <button
                                                                                onClick={() => setUnlinkModalData({ classId: cls.id, studentId: student.id, parentUserId, parentName: parent.name, studentName: student.name })}
                                                                                className="px-2 py-1 text-[10px] font-bold text-rose-600 hover:bg-rose-50 rounded-lg border border-rose-100"
                                                                                title="Hủy liên kết (liên kết nhầm)"
                                                                            >
                                                                                Hủy liên kết
                                                                            </button>
                                                                        )}
                                                                    </div>
                                                                </div>
                                                            );})}
                                                        </div>
                                                    </Card>
                                                ))}
                                                {classStudents.every(s => !Array.isArray(s.parents) || s.parents.length === 0) && (
                                                    <div className="md:col-span-2 text-center py-12 bg-gray-50/50 rounded-2xl border-2 border-dashed">
                                                        <p className="text-sm text-gray-400 italic">Chưa có dữ liệu phụ huynh được cập nhật.</p>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    )}

                                    {getActiveTab(cls.id) === 'sessions' && (
                                        <div className="animate-in fade-in slide-in-from-top-1 duration-300">
                                            <div className="flex justify-between items-center mb-4">
                                                <h4 className="text-xs font-semibold text-slate-500 uppercase tracking-widest">Quản lý Buổi học & Tiến độ</h4>
                                                <button onClick={() => handleAddSession(cls.id)} className="px-4 py-1.5 bg-teal-600 text-white text-xs font-bold rounded-xl hover:bg-teal-700 shadow-md shadow-teal-100 transition-all">+ Thêm buổi học mới</button>
                                            </div>
                                            
                                            {cls.sessions && cls.sessions.length > 0 ? (
                                                <div className="space-y-3">
                                                    {cls.sessions.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()).map(session => (
                                                        <div key={session.id} className="bg-white p-4 rounded-2xl border border-gray-100 shadow-sm hover:border-orange-200 transition-all group">
                                                            <div className="flex justify-between items-center">
                                                                <div className="flex items-center gap-4">
                                                                    <div className="w-12 h-12 bg-orange-50 text-orange-600 rounded-xl flex flex-col items-center justify-center">
                                                                        <span className="text-[10px] font-black uppercase">{new Date(session.date).toLocaleDateString('vi-VN', { month: 'short' })}</span>
                                                                        <span className="text-lg font-black leading-none">{new Date(session.date).getDate()}</span>
                                                                    </div>
                                                                    <div>
                                                                        <h5 className="font-bold text-gray-800 group-hover:text-orange-600 transition-colors">{session.title}</h5>
                                                                        <p className="text-xs text-gray-400 font-medium">
                                                                            {session.grades.filter(g => g.attendance === 'present').length}/{session.grades.length} có mặt • 
                                                                            Điểm TB: {(session.grades.reduce((acc, curr) => acc + (parseFloat(curr.grade as string) || 0), 0) / session.grades.filter(g => g.grade !== '').length || 0).toFixed(1)}
                                                                        </p>
                                                                    </div>
                                                                </div>
                                                                <div className="flex items-center gap-2">
                                                                    <button onClick={() => handleEditSession(cls.id, session)} className="p-2 text-gray-400 hover:bg-gray-100 rounded-lg transition-colors"><PencilIcon className="h-4 w-4" /></button>
                                                                    <button onClick={() => {
                                                                        if (confirm('Xóa buổi học này?')) {
                                                                            const updated = cls.sessions?.filter(s => s.id !== session.id);
                                                                            onUpdateClass({ ...cls, sessions: updated });
                                                                        }
                                                                    }} className="p-2 text-red-400 hover:bg-red-50 rounded-lg transition-colors"><TrashIcon className="h-4 w-4" /></button>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                            ) : (
                                                <div className="text-center py-12 bg-gray-50/50 rounded-2xl border-2 border-dashed">
                                                    <div className="p-4 bg-white rounded-full inline-block shadow-sm mb-3">
                                                        <UserGroupIcon className="h-8 w-8 text-gray-300" />
                                                    </div>
                                                    <p className="text-sm text-gray-400 font-medium">Chưa có buổi học nào được ghi nhận.</p>
                                                    <p className="text-[10px] text-gray-400 uppercase tracking-widest mt-1">Hãy bắt đầu bằng cách thêm buổi học đầu tiên</p>
                                                </div>
                                            )}
                                        </div>
                                    )}

                                    {getActiveTab(cls.id) === 'homework' && (
                                        <div className="animate-in fade-in slide-in-from-top-1 duration-300">
                                            <div className="flex justify-between items-center mb-4">
                                                <h4 className="text-xs font-semibold text-slate-500 uppercase tracking-widest">Danh sách bài tập đã giao</h4>
                                                <button onClick={() => { setClassToAssign(cls.id); setAssignModalType('homework'); setIsAssignModalOpen(true); }} className="px-4 py-1.5 bg-sky-600 text-white text-xs font-bold rounded-xl hover:bg-sky-700 shadow-md shadow-sky-100 transition-all">+ Giao bài mới</button>
                                            </div>
                                            
                                            {classHomeworks.length > 0 ? (
                                                <div className="space-y-4">
                                                    {classHomeworks.sort((a, b) => new Date(b.assignedDate).getTime() - new Date(a.assignedDate).getTime()).map(assignment => {
                                                        const homework = homeworks.find(h => String(h.id) === String(assignment.homeworkId || (assignment as any).homework_id));
                                                        const classSubmissions = submissions.filter(s => String(s.assignmentId) === String(assignment.id));
                                                        
                                                        return (
                                                            <div key={assignment.id} className="bg-white p-4 rounded-2xl border border-gray-100 shadow-sm">
                                                                <div className="flex justify-between items-start mb-4">
                                                                    <div className="flex-1">
                                                                        <h5 className="font-bold text-gray-800">{homework?.title || 'Bài tập'}</h5>
                                                                        <p className="text-[10px] text-gray-400 font-bold uppercase tracking-widest mt-1">Hạn nộp: {new Date(assignment.dueDate).toLocaleDateString('vi-VN')}</p>
                                                                    </div>
                                                                    <div className="flex items-center gap-1 mr-4">
                                                                        <button 
                                                                            onClick={() => { setEditingAssignment(assignment); setClassToAssign(cls.id); setAssignModalType('homework'); setIsAssignModalOpen(true); }}
                                                                            className="p-1.5 text-slate-400 hover:text-sky-600 hover:bg-sky-50 rounded-lg transition-all"
                                                                            title="Sửa bài tập"
                                                                        >
                                                                            <PencilIcon className="h-4 w-4" />
                                                                        </button>
                                                                        <button 
                                                                            onClick={async () => {
                                                                                if (confirm(`Bạn có chắc muốn thu hồi bài tập này?`)) {
                                                                                    const res = await onDeleteAssignment(assignment.id);
                                                                                    if (!res.success && res.conflict) {
                                                                                        setConflictModalData({ id: assignment.id, message: res.message!, count: res.submissionCount! });
                                                                                    }
                                                                                }
                                                                            }}
                                                                            className="p-1.5 text-slate-400 hover:text-rose-600 hover:bg-rose-50 rounded-lg transition-all"
                                                                            title="Thu hồi bài tập"
                                                                        >
                                                                            <TrashIcon className="h-4 w-4" />
                                                                        </button>
                                                                    </div>
                                                                    <div className="text-right">
                                                                        <p className="text-xs font-black text-teal-600">{classSubmissions.length}/{classStudents.length} đã nộp</p>
                                                                        <div className="w-24 h-1 bg-gray-100 rounded-full mt-1 overflow-hidden">
                                                                            <div className="h-full bg-teal-500" style={{ width: `${classStudents.length ? (classSubmissions.length / classStudents.length) * 100 : 0}%` }}></div>
                                                                        </div>
                                                                    </div>
                                                                </div>

                                                                {classSubmissions.length > 0 && (
                                                                    <div className="space-y-2">
                                                                        <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest border-b pb-1">Đã nộp – Chấm điểm</p>
                                                                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                                                                            {classSubmissions.map(sub => {
                                                                                const sId = sub.studentId || (sub as any).student_id;
                                                                                const student = classStudents.find(s => 
                                                                                    String(s.id) === String(sId) || 
                                                                                    s.email === String(sId) || 
                                                                                    (s.studentId && String(s.studentId) === String(sId))
                                                                                );
                                                                                return (
                                                                                    <div key={sub.id} className="p-2 bg-slate-50 rounded-xl flex flex-col gap-1 text-xs">
                                                                                        <div className="flex justify-between items-center">
                                                                                            <span className="font-bold text-gray-700">{student?.name || sId || 'Ẩn danh'}</span>
                                                                                            <div className="flex items-center gap-2">
                                                                                                {isSubmissionGraded(sub.status) ? (
                                                                                                    <div className="flex flex-col items-end gap-1">
                                                                                                        <span className="font-black text-teal-600 bg-teal-50 px-2 py-0.5 rounded-lg">Điểm: {formatGrade(sub.grade, assignment.gradingSystem)}</span>
                                                                                                        <button 
                                                                                                            onClick={() => homework && setGradingSubmission({ homework, submission: sub, student: student!, assignment })}
                                                                                                            disabled={!homework}
                                                                                                            className="text-[9px] font-bold text-sky-600 hover:underline disabled:opacity-50"
                                                                                                        >
                                                                                                            Xem & Chỉnh sửa
                                                                                                        </button>
                                                                                                    </div>
                                                                                                ) : (
                                                                                                    <button 
                                                                                                        onClick={() => homework && setGradingSubmission({ homework, submission: sub, student: student!, assignment })}
                                                                                                        disabled={!homework}
                                                                                                        title={!homework ? 'Bài tập không tìm thấy trong thư viện' : undefined}
                                                                                                        className="px-3 py-1.5 bg-sky-600 text-white font-black rounded-xl hover:bg-sky-700 shadow-md shadow-sky-100 transition-all active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed"
                                                                                                    >
                                                                                                        Chấm điểm
                                                                                                    </button>
                                                                                                )}
                                                                                            </div>
                                                                                        </div>
                                                                                        {(sub as any).content?.attachments?.length > 0 && (
                                                                                            <div className="flex flex-wrap gap-1 mt-1">
                                                                                                {(sub as any).content.attachments.map((att: { url: string; type: string; name: string }, i: number) => (
                                                                                                    <a key={i} href={att.url} target="_blank" rel="noopener noreferrer" className="text-[10px] text-sky-600 hover:underline truncate max-w-[120px]">
                                                                                                        {att.type === 'video' ? '🎬 ' : '🖼 '}{att.name}
                                                                                                    </a>
                                                                                                ))}
                                                                                            </div>
                                                                                        )}
                                                                                    </div>
                                                                                );
                                                                            })}
                                                                        </div>
                                                                    </div>
                                                                )}
                                                                {classStudents.length > classSubmissions.length && (
                                                                    <div className="mt-3 pt-3 border-t border-gray-100 space-y-2">
                                                                        <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Chưa nộp</p>
                                                                        <div className="flex flex-wrap gap-1.5">
                                                                            {classStudents.filter(s => !classSubmissions.some(sub => {
                                                                                const sId = sub.studentId || (sub as any).student_id;
                                                                                return String(sId) === String(s.id) || 
                                                                                       String(sId) === s.email || 
                                                                                       (s.studentId && String(sId) === String(s.studentId));
                                                                            })).map(s => (
                                                                                <span key={s.id} className="px-2 py-0.5 bg-amber-50 text-amber-700 rounded-lg text-[10px] font-medium">{s.name || s.realName || s.email}</span>
                                                                            ))}
                                                                        </div>
                                                                    </div>
                                                                )}
                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                            ) : (
                                                <div className="text-center py-12 bg-gray-50/50 rounded-2xl border-2 border-dashed">
                                                    <p className="text-sm text-gray-400 italic">Chưa giao bài tập nào cho lớp này.</p>
                                                </div>
                                            )}
                                        </div>
                                    )}

                                    {getActiveTab(cls.id) === 'tasks' && (
                                        <div className="animate-in fade-in slide-in-from-top-1 duration-300">
                                            <div className="flex justify-between items-center mb-4">
                                                <h4 className="text-xs font-semibold text-slate-500 uppercase tracking-widest">Danh sách nhiệm vụ đã giao</h4>
                                                <button onClick={() => { setClassToAssign(cls.id); setAssignModalType('task'); setIsAssignModalOpen(true); }} className="px-4 py-1.5 bg-teal-600 text-white text-xs font-bold rounded-xl hover:bg-teal-700 shadow-md shadow-teal-100 transition-all">+ Giao nhiệm vụ</button>
                                            </div>
                                            
                                            {classTasks.length > 0 ? (
                                                <div className="space-y-4">
                                                    {classTasks.sort((a, b) => new Date((b as any).assigned_date || b.assignedDate || 0).getTime() - new Date((a as any).assigned_date || a.assignedDate || 0).getTime()).map(assignment => {
                                                        const taskSubmissions = submissions.filter(s => String(s.assignmentId) === String(assignment.id));
                                                        return (
                                                            <div key={assignment.id} className="bg-white p-4 rounded-2xl border border-gray-100 shadow-sm">
                                                                <div className="flex justify-between items-start mb-2">
                                                                    <div className="flex-1">
                                                                        <h5 className="font-bold text-gray-800">{(assignment as any).title || assignment.title || 'Nhiệm vụ'}</h5>
                                                                        <p className="text-[10px] text-gray-400 font-bold uppercase tracking-widest mt-1">
                                                                            {(assignment as any).due_date || assignment.dueDate ? `Hạn: ${new Date((assignment as any).due_date || assignment.dueDate || '').toLocaleDateString('vi-VN')}` : (assignment as any).scheduled_date || assignment.scheduledDate ? `Bắt đầu: ${new Date((assignment as any).scheduled_date || assignment.scheduledDate || '').toLocaleDateString('vi-VN')}` : 'Không hạn'}
                                                                        </p>
                                                                    </div>
                                                                    <div className="flex items-center gap-1 mr-4">
                                                                        <button 
                                                                            onClick={() => { setEditingAssignment(assignment); setClassToAssign(cls.id); setAssignModalType('task'); setIsAssignModalOpen(true); }}
                                                                            className="p-1.5 text-slate-400 hover:text-sky-600 hover:bg-sky-50 rounded-lg transition-all"
                                                                            title="Sửa nhiệm vụ"
                                                                        >
                                                                            <PencilIcon className="h-4 w-4" />
                                                                        </button>
                                                                        <button 
                                                                            onClick={async () => {
                                                                                if (confirm(`Bạn có chắc muốn thu hồi nhiệm vụ này?`)) {
                                                                                    const res = await onDeleteAssignment(assignment.id);
                                                                                    if (!res.success && res.conflict) {
                                                                                        setConflictModalData({ id: assignment.id, message: res.message!, count: res.submissionCount! });
                                                                                    }
                                                                                }
                                                                            }}
                                                                            className="p-1.5 text-slate-400 hover:text-rose-600 hover:bg-rose-50 rounded-lg transition-all"
                                                                            title="Thu hồi nhiệm vụ"
                                                                        >
                                                                            <TrashIcon className="h-4 w-4" />
                                                                        </button>
                                                                    </div>
                                                                <div className="text-right">
                                                                    <p className="text-xs font-black text-teal-600">
                                                                        {taskSubmissions.length}/{classStudents.length} {assignment.gradingSystem === 'none' ? 'đã xong' : 'đã xác nhận'}
                                                                    </p>
                                                                    <div className="w-24 h-1 bg-gray-100 rounded-full mt-1 overflow-hidden">
                                                                        <div className="h-full bg-teal-500" style={{ width: `${classStudents.length ? (taskSubmissions.length / classStudents.length) * 100 : 0}%` }}></div>
                                                                    </div>
                                                                </div>
                                                                </div>
                                                                {((assignment as any).instructions || assignment.instructions) && (
                                                                    <p className="text-sm text-gray-600 mt-2 line-clamp-2">{(assignment as any).instructions || assignment.instructions}</p>
                                                                )}
                                                                {assignment.attachments && assignment.attachments.length > 0 && (
                                                                    <div className="mt-3 flex flex-wrap gap-2 animate-in fade-in slide-in-from-top-1 duration-300">
                                                                        {assignment.attachments.map((att, i) => (
                                                                            <button 
                                                                                key={i} 
                                                                                onClick={() => window.open(formatAssetUrl(att.url), '_blank')}
                                                                                className="px-2 py-1.5 bg-slate-50 text-slate-600 rounded-xl text-[9px] font-black border border-slate-100 flex items-center gap-1.5 hover:bg-teal-50 hover:text-teal-700 hover:border-teal-100 transition-all shadow-sm"
                                                                            >
                                                                                <span>{att.type === 'image' ? '🖼' : att.type === 'video' ? '🎬' : att.type === 'audio' ? '🎵' : '📄'}</span>
                                                                                <span className="truncate max-w-[150px]">{att.name}</span>
                                                                            </button>
                                                                        ))}
                                                                    </div>
                                                                )}
                                                                    <div className="mt-3 pt-3 border-t border-gray-100 space-y-2">
                                                                        <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest">
                                                                            {assignment.gradingSystem === 'none' ? 'Đã hoàn thành – Nhận xét' : 'Đã xác nhận – Chấm điểm'}
                                                                        </p>
                                                                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                                                                            {taskSubmissions.map(sub => {
                                                                                const sId = sub.studentId || (sub as any).student_id;
                                                                                const student = classStudents.find(s => 
                                                                                    String(s.id) === String(sId) || 
                                                                                    s.email === String(sId) || 
                                                                                    (s.studentId && String(s.studentId) === String(sId))
                                                                                );
                                                                                return (
                                                                                    <div key={sub.id} className="p-2 bg-slate-50 rounded-xl flex flex-col gap-1 text-xs">
                                                                                        <div className="flex justify-between items-center">
                                                                                            <span className="font-bold text-gray-700">{student?.name || sId || 'Ẩn danh'}</span>
                                                                                            {isSubmissionGraded(sub.status) ? (
                                                                                                <div className="flex flex-col items-end gap-1">
                                                                                                    <span className="font-black text-teal-600 bg-teal-50 px-2 py-0.5 rounded-lg">
                                                                                                        {assignment.gradingSystem === 'none' ? 'Đã xem' : `Điểm: ${formatGrade(sub.grade, assignment.gradingSystem)}`}
                                                                                                    </span>
                                                                                                    <button 
                                                                                                        onClick={() => setGradingSubmission({ 
                                                                                                            homework: { id: assignment.id, title: (assignment as any).title || assignment.title, tasks: [], generalInstructions: (assignment as any).instructions || assignment.instructions } as Homework, 
                                                                                                            submission: sub, 
                                                                                                            student: student!,
                                                                                                            assignment
                                                                                                        })}
                                                                                                        className="text-[9px] font-bold text-teal-600 hover:underline"
                                                                                                    >
                                                                                                        Xem & Chỉnh sửa
                                                                                                    </button>
                                                                                                </div>
                                                                                            ) : (
                                                                                                <button 
                                                                                                    onClick={() => setGradingSubmission({ 
                                                                                                        homework: { id: assignment.id, title: (assignment as any).title || assignment.title, tasks: [], generalInstructions: (assignment as any).instructions || assignment.instructions } as Homework, 
                                                                                                        submission: sub, 
                                                                                                        student: student!,
                                                                                                        assignment
                                                                                                    })}
                                                                                                    className="px-3 py-1.5 bg-teal-600 text-white font-black rounded-xl hover:bg-teal-700 shadow-md shadow-teal-100 transition-all active:scale-95"
                                                                                                >
                                                                                                    {assignment.gradingSystem === 'none' ? 'Xem & Nhận xét' : 'Chấm điểm'}
                                                                                                </button>
                                                                                            )}
                                                                                        </div>
                                                                                        {(sub as any).content?.attachments?.length > 0 && (
                                                                                            <div className="flex flex-wrap gap-1 mt-1">
                                                                                                {(sub as any).content.attachments.map((att: { url: string; type: string; name: string }, i: number) => (
                                                                                                    <a key={i} href={att.url} target="_blank" rel="noopener noreferrer" className="text-[10px] text-sky-600 hover:underline truncate max-w-[120px]">
                                                                                                        {att.type === 'video' ? '🎬 ' : '🖼 '}{att.name}
                                                                                                    </a>
                                                                                                ))}
                                                                                            </div>
                                                                                        )}
                                                                                    </div>
                                                                                );
                                                                            })}
                                                                        </div>
                                                                    </div>
                                                                    <div className="mt-3 pt-3 border-t border-gray-100 space-y-2">
                                                                        <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest">
                                                                            {assignment.gradingSystem === 'none' ? 'Chưa hoàn thành' : 'Chưa xác nhận'}
                                                                        </p>
                                                                        <div className="flex flex-wrap gap-1.5">
                                                                            {classStudents.filter(s => !taskSubmissions.some(sub => {
                                                                                const sId = sub.studentId || (sub as any).student_id;
                                                                                return String(sId) === String(s.id) || 
                                                                                       String(sId) === s.email || 
                                                                                       (s.studentId && String(sId) === String(s.studentId));
                                                                            })).map(s => (
                                                                                <span key={s.id} className="px-2 py-0.5 bg-amber-50 text-amber-700 rounded-lg text-[10px] font-medium">{s.name || s.realName || s.email}</span>
                                                                            ))}
                                                                        </div>
                                                                    </div>
                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                            ) : (
                                                <div className="text-center py-12 bg-gray-50/50 rounded-2xl border-2 border-dashed">
                                                    <ClipboardDocumentCheckIcon className="h-12 w-12 text-teal-200 mx-auto mb-3" />
                                                    <p className="text-sm text-gray-400 italic">Chưa giao nhiệm vụ nào cho lớp này.</p>
                                                    <button onClick={() => { setClassToAssign(cls.id); setAssignModalType('task'); setIsAssignModalOpen(true); }} className="mt-3 px-4 py-2 bg-teal-600 text-white text-xs font-bold rounded-xl hover:bg-teal-700">+ Giao nhiệm vụ đầu tiên</button>
                                                </div>
                                            )}
                                        </div>
                                    )}

                                    {getActiveTab(cls.id) === 'announcements' && (
                                        <div className="animate-in fade-in slide-in-from-top-1 duration-300">
                                            <div className="flex justify-between items-center mb-4">
                                                <h4 className="text-xs font-semibold text-slate-500 uppercase tracking-widest">Thông báo đã đăng</h4>
                                                <button onClick={() => { setClassToAssign(cls.id); setAssignModalType('announcement'); setIsAssignModalOpen(true); }} className="px-4 py-1.5 bg-amber-500 text-white text-xs font-bold rounded-xl hover:bg-amber-600 shadow-md shadow-amber-100 transition-all">+ Đăng thông báo</button>
                                            </div>
                                            
                                            {classAnnouncements.length > 0 ? (
                                                <div className="space-y-4">
                                                    {classAnnouncements.sort((a, b) => new Date((b as any).assigned_date || b.assignedDate || 0).getTime() - new Date((a as any).assigned_date || a.assignedDate || 0).getTime()).map(assignment => (
                                                        <div key={assignment.id} className="bg-white p-4 rounded-2xl border border-gray-100 shadow-sm">
                                                            <div className="flex items-start gap-3">
                                                                <div className="w-10 h-10 bg-amber-100 text-amber-600 rounded-xl flex items-center justify-center shrink-0">
                                                                    <MegaphoneIconSolid className="h-5 w-5" />
                                                                </div>
                                                                <div className="flex-1 min-w-0">
                                                                    <div className="flex justify-between items-start">
                                                                        <h5 className="font-bold text-gray-800 truncate pr-2">{(assignment as any).title || assignment.title || 'Thông báo'}</h5>
                                                                        <div className="flex items-center gap-1 shrink-0">
                                                                            <button 
                                                                                onClick={() => { setEditingAssignment(assignment); setClassToAssign(cls.id); setAssignModalType('announcement'); setIsAssignModalOpen(true); }}
                                                                                className="p-1 text-slate-400 hover:text-amber-600 hover:bg-amber-50 rounded transition-all"
                                                                                title="Sửa thông báo"
                                                                            >
                                                                                <PencilIcon className="h-3.5 w-3.5" />
                                                                            </button>
                                                                            <button 
                                                                                onClick={async () => {
                                                                                    if (confirm(`Bạn có chắc muốn gỡ bỏ thông báo này?`)) {
                                                                                        await onDeleteAssignment(assignment.id);
                                                                                    }
                                                                                }}
                                                                                className="p-1 text-slate-400 hover:text-rose-600 hover:bg-rose-50 rounded transition-all"
                                                                                title="Gỡ thông báo"
                                                                            >
                                                                                <TrashIcon className="h-3.5 w-3.5" />
                                                                            </button>
                                                                        </div>
                                                                    </div>
                                                                    <p className="text-[10px] text-gray-400 font-bold uppercase tracking-widest mt-1">
                                                                        {(assignment as any).scheduled_date || assignment.scheduledDate ? new Date((assignment as any).scheduled_date || assignment.scheduledDate || '').toLocaleString('vi-VN') : (assignment as any).assigned_date || assignment.assignedDate ? new Date((assignment as any).assigned_date || assignment.assignedDate || '').toLocaleString('vi-VN') : '---'}
                                                                    </p>
                                                                    {((assignment as any).instructions || assignment.instructions) && (
                                                                        <p className="text-sm text-gray-600 mt-2 whitespace-pre-wrap">{(assignment as any).instructions || assignment.instructions}</p>
                                                                    )}
                                                                </div>
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                            ) : (
                                                <div className="text-center py-12 bg-gray-50/50 rounded-2xl border-2 border-dashed">
                                                    <MegaphoneIconSolid className="h-12 w-12 text-amber-200 mx-auto mb-3" />
                                                    <p className="text-sm text-gray-400 italic">Chưa có thông báo nào cho lớp này.</p>
                                                    <button onClick={() => { setClassToAssign(cls.id); setAssignModalType('announcement'); setIsAssignModalOpen(true); }} className="mt-3 px-4 py-2 bg-amber-500 text-white text-xs font-bold rounded-xl hover:bg-amber-600">+ Đăng thông báo đầu tiên</button>
                                                </div>
                                            )}
                                        </div>
                                    )}

                                    {getActiveTab(cls.id) === 'reports' && (
                                        <div className="animate-in fade-in slide-in-from-top-1 duration-300">
                                            <h4 className="text-xs font-semibold text-slate-500 uppercase tracking-widest mb-4">Tổng hợp kết quả học tập</h4>
                                            <div className="bg-white border rounded-2xl overflow-hidden shadow-sm">
                                                <div className="overflow-x-auto">
                                                    <table className="w-full text-sm">
                                                        <thead className="bg-slate-50 border-b">
                                                            <tr>
                                                                <th className="text-left p-4 text-xs font-semibold text-slate-500 uppercase tracking-widest">Học sinh</th>
                                                                <th className="text-center p-4 text-xs font-semibold text-slate-500 uppercase tracking-widest">Chuyên cần</th>
                                                                <th className="text-center p-4 text-xs font-semibold text-slate-500 uppercase tracking-widest">Điểm TB (Buổi học)</th>
                                                                <th className="text-center p-4 text-xs font-semibold text-slate-500 uppercase tracking-widest">Bài tập đã nộp</th>
                                                                <th className="text-center p-4 text-xs font-semibold text-slate-500 uppercase tracking-widest">Xếp loại</th>
                                                                <th className="text-right p-4 text-xs font-semibold text-slate-500 uppercase tracking-widest">Thao tác</th>
                                                            </tr>
                                                        </thead>
                                                        <tbody className="divide-y divide-gray-50">
                                                            {classStudents.map(student => {
                                                                // Calculate stats from sessions
                                                                const studentSessions = cls.sessions?.map(s => s.grades.find(g => String(g.studentId) === String(student.id))).filter(g => !!g) || [];
                                                                const attendanceRate = studentSessions.length > 0 ? (studentSessions.filter(s => s?.attendance === 'present').length / studentSessions.length * 100).toFixed(0) : '---';
                                                                const avgGrade = studentSessions.filter(s => s?.grade !== '').length > 0 
                                                                    ? (studentSessions.reduce((acc, curr) => acc + (parseFloat(curr?.grade as string) || 0), 0) / studentSessions.filter(s => s?.grade !== '').length).toFixed(1)
                                                                    : '---';

                                                                const classAssignments = classHomeworks;
                                                                const studentSubmissions = submissions.filter(s => {
                                                                    const sId = s.studentId || (s as any).student_id;
                                                                    return String(sId) === String(student.id) || 
                                                                           String(sId) === student.email || 
                                                                           (student.studentId && String(sId) === String(student.studentId));
                                                                });
                                                                const submittedCount = classAssignments.filter(a => studentSubmissions.some(s => s.assignmentId === a.id)).length;

                                                                return (
                                                                    <tr key={student.id} className="hover:bg-slate-50/50 transition-colors">
                                                                        <td className="p-4 font-bold text-gray-800">{student.name}{student.realName ? ` (${student.realName})` : ''}</td>
                                                                        <td className="p-4 text-center text-xs font-bold text-blue-600">{attendanceRate}%</td>
                                                                        <td className="p-4 text-center font-black text-teal-600 text-lg">{avgGrade}</td>
                                                                        <td className="p-4 text-center">
                                                                            <span className="text-xs font-bold text-amber-600">{submittedCount}/{classAssignments.length}</span>
                                                                        </td>
                                                                        <td className="p-4 text-center">
                                                                            <span className={`px-2 py-0.5 rounded-full text-[10px] font-black uppercase ${
                                                                                parseFloat(avgGrade) >= 8 ? 'bg-green-100 text-green-700' :
                                                                                parseFloat(avgGrade) >= 6.5 ? 'bg-blue-100 text-blue-700' :
                                                                                avgGrade === '---' ? 'bg-gray-100 text-gray-400' : 'bg-red-100 text-red-700'
                                                                            }`}>
                                                                                {parseFloat(avgGrade) >= 8 ? 'Giỏi' : parseFloat(avgGrade) >= 6.5 ? 'Khá' : avgGrade === '---' ? 'N/A' : 'TB/Yếu'}
                                                                            </span>
                                                                        </td>
                                                                        <td className="p-4 text-right">
                                                                            <button 
                                                                                onClick={() => { setStudentForReport(student); setIsReportModalOpen(true); }}
                                                                                className="text-teal-600 hover:text-teal-700 font-bold text-xs"
                                                                            >
                                                                                Chi tiết
                                                                            </button>
                                                                        </td>
                                                                    </tr>
                                                                );
                                                            })}
                                                        </tbody>
                                                    </table>
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </Card>
                        );
                    })}
                </div>
            )}
            {rejectModalRequestId && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm" onClick={() => setRejectModalRequestId(null)}>
                    <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full m-4 p-6" onClick={e => e.stopPropagation()}>
                        <h3 className="text-lg font-bold text-slate-800 mb-2">Từ chối yêu cầu tham gia</h3>
                        <p className="text-sm text-slate-500 mb-4">Nhập lý do từ chối để người yêu cầu biết (tùy chọn):</p>
                        <textarea
                            value={rejectReason}
                            onChange={e => setRejectReason(e.target.value)}
                            placeholder="VD: Lớp đã đủ sĩ số, vui lòng liên hệ giáo viên để được tư vấn lớp phù hợp hơn."
                            rows={4}
                            className="w-full p-3 border-2 border-slate-100 rounded-xl focus:border-rose-300 focus:ring-2 focus:ring-rose-100 outline-none text-sm resize-none"
                        />
                        <div className="flex gap-3 mt-6">
                            <button onClick={() => setRejectModalRequestId(null)} className="flex-1 py-2.5 bg-slate-100 text-slate-700 font-bold rounded-xl hover:bg-slate-200 transition-colors">Hủy</button>
                            <button 
                                onClick={() => rejectModalRequestId && handleRejectRequest(rejectModalRequestId, rejectReason)} 
                                className="flex-1 py-2.5 bg-rose-600 text-white font-bold rounded-xl hover:bg-rose-700 transition-colors"
                            >
                                Xác nhận từ chối
                            </button>
                        </div>
                    </div>
                </div>
            )}
            <SessionModal 
                isOpen={isSessionModalOpen} 
                onClose={() => setIsSessionModalOpen(false)} 
                session={editingSession}
                onSave={(sessionData) => currentSessionClassId && handleSaveSession(currentSessionClassId, sessionData)}
                students={currentSessionClassId ? (classes.find(c => c.id === currentSessionClassId)?.classType === 'online' ? approvedStudents : classes.find(c => c.id === currentSessionClassId)?.studentIds.map(id => students.find(s => String(s.id) === String(id))).filter((s): s is Student => !!s) || []) : []}
            />
            {gradingSubmission && (
                <GradingModal 
                    homework={gradingSubmission.homework}
                    submission={gradingSubmission.submission}
                    student={gradingSubmission.student}
                    assignment={gradingSubmission.assignment}
                    onClose={() => setGradingSubmission(null)}
                    onSaveGrade={onGradeSubmission}
                />
            )}
            {unlinkModalData && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm" onClick={() => setUnlinkModalData(null)}>
                    <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full m-4 p-6" onClick={e => e.stopPropagation()}>
                        <h3 className="text-lg font-bold text-slate-800 mb-2 font-black uppercase tracking-tight">Hủy liên kết phụ huynh</h3>
                        <p className="text-sm text-slate-500 mb-4 leading-relaxed">
                            Bạn đang thực hiện hủy liên kết giữa phụ huynh <span className="font-bold text-slate-800">"{unlinkModalData.parentName}"</span> và học sinh <span className="font-bold text-slate-800">"{unlinkModalData.studentName}"</span>.
                        </p>
                        <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2">Lý do hủy liên kết (tùy chọn):</p>
                        <textarea
                            value={unlinkReason}
                            onChange={e => setUnlinkReason(e.target.value)}
                            placeholder="VD: Phụ huynh đăng ký nhầm tài khoản, thay đổi thông tin người giám hộ..."
                            rows={3}
                            className="w-full p-3 border-2 border-slate-100 rounded-xl focus:border-teal-300 focus:ring-2 focus:ring-teal-100 outline-none text-sm resize-none"
                        />
                        <div className="flex gap-3 mt-6">
                            <button onClick={() => setUnlinkModalData(null)} className="flex-1 py-3 bg-slate-100 text-slate-700 font-bold rounded-xl hover:bg-slate-200 transition-all active:scale-95">Quay lại</button>
                            <button 
                                onClick={() => handleUnlinkParent(unlinkModalData.classId, unlinkModalData.studentId, unlinkModalData.parentUserId, unlinkReason)} 
                                className="flex-[1.5] py-3 bg-rose-600 text-white font-bold rounded-xl hover:bg-rose-700 transition-all shadow-lg shadow-rose-100 active:scale-95"
                            >
                                Xác nhận hủy
                            </button>
                        </div>
                    </div>
                </div>
            )}
            {conflictModalData && (
                <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60 backdrop-blur-sm" onClick={() => setConflictModalData(null)}>
                    <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full m-4 p-6 border-t-4 border-rose-500" onClick={e => e.stopPropagation()}>
                        <div className="flex items-center gap-3 mb-4">
                            <div className="p-3 bg-rose-50 rounded-full">
                                <InformationCircleIcon className="h-6 w-6 text-rose-600" />
                            </div>
                            <h3 className="text-lg font-black text-slate-800 uppercase tracking-tight">Cảnh báo thu hồi</h3>
                        </div>
                        <p className="text-sm text-slate-600 mb-6 leading-relaxed">
                            {conflictModalData.message}
                            <br /><br />
                            Nếu bạn vẫn muốn <span className="font-bold text-rose-600">XÓA HOÀN TOÀN</span>, toàn bộ bài làm của học sinh sẽ bị mất và không thể khôi phục.
                        </p>
                        <div className="flex gap-3">
                            <button onClick={() => setConflictModalData(null)} className="flex-1 py-3 bg-slate-100 text-slate-700 font-bold rounded-xl hover:bg-slate-200 transition-all active:scale-95">Quay lại</button>
                            <button 
                                onClick={async () => {
                                    const id = conflictModalData.id;
                                    setConflictModalData(null);
                                    await onDeleteAssignment(id, true);
                                }} 
                                className="flex-[1.5] py-3 bg-rose-600 text-white font-bold rounded-xl hover:bg-rose-700 transition-all shadow-lg shadow-rose-100 active:scale-95"
                            >
                                Vẫn muốn xóa
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ClassManagement;